import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  PageHeader, Modal, DetailModal, DetailPanel, ConfirmDialog, Badge, Spinner, TableSkeletonRows, EmptyState, useToast,
  usePagination, Pagination, useSort, SortTh, SearchableSelect, FilterBar, FilterSelect, DateRangePicker,
  claimStatusBadgeMap, assessmentStatusBadgeMap, observationRatingBadgeMap, attendanceBadgeMap, sessionStatusBadgeMap,
  InlineSpinner,
} from '../components/UI';
import FileAttachments from '../components/FileAttachments';
import { ComponentSetupModal } from './RecordPages';
import { usePermissions } from '../context/PermissionsContext';
import { fmtDate, fmtDateTime, fmtHours } from '../utils/format';
import ClaimSignOffModal from '../components/ClaimSignOffModal';
import { generateAvatarColor } from '../utils/avatarColor';
import socket from '../utils/socket';
import {
  ScheduleCardGrid, MonthGrid, RosterModal, ViewModeToggle,
  getRangeForMode, shiftAnchor, scheduleColumns, ymd,
} from '../components/WeeklyScheduleGrid';

function scheduleRangeLabel(mode, anchorDate, range) {
  if (mode === 'day') return anchorDate.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  if (mode === 'month') return anchorDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  return `Week of ${fmtDate(range.monday)} – ${fmtDate(range.sunday)}`;
}

const SCHEDULE_TODAY_LABEL = { day: 'Today', week: 'This week', month: 'This month' };

function fmtMoney(v) {
  const n = Number(v);
  return isNaN(n) ? '—' : `S$${n.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function trainerInitials(name = '') {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  return (words[0] || 'TR').slice(0, 2).toUpperCase();
}

function TrainerAvatar({ name, avatarUrl, size = 38 }) {
  const [imageFailed, setImageFailed] = useState(false);
  const [fallbackColor] = useState(() => generateAvatarColor());
  useEffect(() => setImageFailed(false), [avatarUrl]);
  return (
    <span className="claim-completion-avatar" style={{ width: size, height: size, flexBasis: size, background: avatarUrl && !imageFailed ? 'var(--sms-sur2)' : fallbackColor }}>
      {avatarUrl && !imageFailed
        ? <img src={avatarUrl} alt="" onError={() => setImageFailed(true)} />
        : <span aria-hidden="true">{trainerInitials(name)}</span>}
    </span>
  );
}

function feeRateSetupNavigation(claim) {
  return {
    state: {
      feeRateSetup: {
        trainerId: claim?.trainer_id,
        trainerName: claim?.trainer_name,
        feeType: claim?.fee_type,
      },
    },
  };
}

function claimRateLabel(claim) {
  const rate = Number(claim?.available_rate ?? claim?.rate);
  return Number.isFinite(rate) && rate > 0 ? `${fmtMoney(rate)}/${isPerPaxClaim(claim) ? 'pax' : 'hr'}` : 'Not set';
}

function claimRateValue(claim) {
  const rate = Number(claim?.available_rate ?? claim?.rate);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
}

function claimHasSavedRate(claim) {
  return Boolean(claim?.fee_rate_id) && Number(claim?.rate) > 0;
}

const CLAIM_FEE_TYPE_MAP = {
  attendance: { label: 'Attendance', cls: 'badge-present' },
  assessment: { label: 'Assessment', cls: 'badge-graduated' },
  assessment_per_pax: { label: 'Assessment F.A. 1.2.', cls: 'badge-late' },
  assessment_final: { label: 'Assessment F.A. 2.2.', cls: 'badge-late' },
  course_preview: { label: 'Course Preview', cls: 'badge-active' },
  interview: { label: 'Interview', cls: 'badge-late' },
};

// Claims without an attendance session; an admin completes their time/amount.
const isManualClaimType = type => ['course_preview', 'interview', 'assessment'].includes(type);
// Assessment F.A. 1.2. is paid per student (pax), not per hour.
const isPerPaxClaim = claim => claim?.fee_type === 'interview' || (claim?.fee_type === 'assessment' && claim?.assessment_basis === 'per_pax');
const claimFeeTypeKey = claim => {
  const type = claim?.fee_type || 'attendance';
  return type === 'assessment' && claim?.assessment_basis && claim.assessment_basis !== 'duration'
    ? `assessment_${claim.assessment_basis}`
    : type;
};

function claimAmountPending(claim) {
  const amount = Number(claim?.amount);
  return !Number.isFinite(amount) || amount <= 0;
}

function claimAmountLabel(claim) {
  if (claimAmountPending(claim)) return 'Pending Amount';
  return fmtMoney(claim.amount);
}

function timeToMinutes(value) {
  const match = String(value || '').match(/^(\d{2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour > 23 || minute > 59 ? null : hour * 60 + minute;
}

function claimDurationHours(startTime, endTime) {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (start === null || end === null || end <= start) return null;
  return Number(((end - start) / 60).toFixed(2));
}

function fmtClaimTime(value) {
  return value ? String(value).slice(0, 5) : '—';
}

function claimTimeRange(claim) {
  if (!claim?.start_time || !claim?.end_time) return '—';
  return `${fmtClaimTime(claim.start_time)}–${fmtClaimTime(claim.end_time)}`;
}

// Session-based claims are stored as fee_type 'attendance' (a stored
// 'assessment' means a manual claim an admin must complete), so their type
// comes from the sessions inside: all Assessment → Assessment, all Training →
// Attendance, a mix → both badges.
function ClaimFeeType({ claim }) {
  const key = claimFeeTypeKey(claim);
  if (key !== 'attendance') return <Badge value={key} map={CLAIM_FEE_TYPE_MAP} />;
  const sessionTypes = (claim.items?.length ? claim.items.map(i => i.session_type) : [claim.session_type])
    .map(t => String(t || '').trim().toLowerCase());
  const keys = [...new Set(sessionTypes.map(t => (t === 'assessment' ? 'assessment' : 'attendance')))];
  return <span className="d-inline-flex flex-wrap gap-1">{keys.map(k => <Badge key={k} value={k} map={CLAIM_FEE_TYPE_MAP} />)}</span>;
}

function ordinal(n) {
  n = Number(n);
  if (!Number.isFinite(n)) return String(n);
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Mirrors the backend's claimWindowStatus: adds calendar days to an actual
// date (using the current month as the illustrative example) so a due date
// near month-end correctly rolls into the next month instead of reading as
// an impossible day like "the 33rd".
function claimsCloseLabel(openDay, dueDays) {
  const now = new Date();
  const openDate = new Date(now.getFullYear(), now.getMonth(), Number(openDay) || 1);
  const closeDate = new Date(openDate);
  closeDate.setDate(closeDate.getDate() + Number(dueDays));
  const sameMonth = closeDate.getMonth() === openDate.getMonth();
  return sameMonth
    ? ordinal(closeDate.getDate())
    : `${ordinal(closeDate.getDate())} of ${closeDate.toLocaleDateString('en-GB', { month: 'long' })}`;
}

// Same icon-badge treatment as DashboardPage's StatCard (color-mix tinted
// icon chip + gradient wash), minus the Link/Sparkline — these summary
// numbers don't navigate anywhere and have no history series to plot.
// `onClick` (when passed) turns the card into a shortcut that applies the
// matching status filter to the table below; `active` outlines it once that
// filter is the one currently applied, so the card doubles as a status
// indicator for what's being shown.
function ClaimStatCard({ label, value, icon, color, sub, onClick, active }) {
  return (
    <div className={`sms-stat-card sms-claim-stat-card h-100${onClick ? ' sms-stat-card-clickable' : ''}`}
      role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }) : undefined}
      style={{
        position: 'relative', overflow: 'hidden',
        background: `linear-gradient(180deg, color-mix(in srgb, ${color} 7%, var(--sms-sur)), var(--sms-sur) 65%)`,
        borderColor: active ? color : undefined,
        boxShadow: active ? `0 0 0 1px ${color} inset` : undefined,
      }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
        <div className="sms-claim-stat-icon" style={{ width: 34, height: 34, borderRadius: 9, background: `color-mix(in srgb, ${color} 14%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`bi ${icon}`} style={{ color, fontSize: 16 }} />
        </div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--sms-txt)', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// Collapsible header for a trainer detail section — starts collapsed so an
// empty/uninteresting section (e.g. no claims) doesn't take up modal space
// until the admin asks to see it. Same icon-badge-and-button treatment as
// the Student Profile detail modal's sections (StudentsPage.jsx).
const SECTION_STYLE = {
  claims:       { icon: 'bi-receipt',         color: 'var(--sms-stat-amber)' },
  assessments:  { icon: 'bi-journal-check',   color: 'var(--sms-stat-cyan)' },
  observations: { icon: 'bi-eye',             color: 'var(--sms-stat-violet)' },
};

function Section({ sectionKey, label, open, onToggle, children }) {
  const { icon, color } = SECTION_STYLE[sectionKey];
  return (
    <div className="mt-3">
      <button type="button" onClick={onToggle}
        className="sms-btn sms-btn-outline w-100 d-flex justify-content-between align-items-center" style={{ fontSize: 12 }}>
        <span className="d-flex align-items-center gap-2">
          <span style={{ width: 24, height: 24, borderRadius: 7, background: color + '1f', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className={`bi ${icon}`} style={{ color, fontSize: 13 }} />
          </span>
          {label}
        </span>
        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

// ─── TRAINER DIRECTORY ─────────────────────────────────────────────────────────
const TRAINER_EDIT_EMPTY = { licence_expiry: '', bank_name: '', bank_account_number: '', qualifications: [], experience: [] };

// qualifications/experience are stored as a JSON array of {text, year} objects
// — qualifications track the year obtained, experience tracks years of
// duration. Falls back to wrapping old plain-string entries so nothing
// already saved breaks.
function parseEntryList(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    return list.map(e => typeof e === 'string' ? { text: e, year: '' } : { text: e.text || '', year: e.year ?? e.years ?? '' });
  } catch {
    return [{ text: raw, year: '' }];
  }
}

// Icon-badged card wrapper for one field group in the Edit Trainer Profile
// form — groups Licence/Qualifications/Work Experience/Attachments into
// distinct blocks instead of one long stacked list. `color` must be one of
// the app's --sms-stat-* / --sms-acc tokens so the badge tint and icon
// resolve to values already verified against --sms-sur2 in both themes
// (see index.css) — same color-mix badge pattern as DashboardPage's StatCard.
function FormCard({ icon, color, title, action, children }) {
  return (
    <div className="sms-card" style={{ padding: 14 }}>
      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 10 }}>
        <div className="d-flex align-items-center gap-2">
          <span style={{
            width: 22, height: 22, borderRadius: 6, flexShrink: 0,
            background: `color-mix(in srgb, ${color} 12%, var(--sms-sur2))`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className={`bi ${icon}`} style={{ color, fontSize: 12 }} />
          </span>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{title}</span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// Repeatable entries (one per qualification/experience item), each with a
// short year/duration number alongside the description — same add/remove-row
// pattern as Component Master's component list.
function EntryListField({ placeholder, yearLabel, entries, onChange, onRemove }) {
  if (!entries.length) return <div style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>None added yet.</div>;
  return entries.map((entry, i) => (
    <div key={i} className="d-flex gap-2 mb-2">
      <input type="text" className="sms-form-control" value={entry.text} placeholder={placeholder}
        onChange={e => onChange(i, { ...entry, text: e.target.value })} />
      <input type="number" min="0" className="sms-form-control" value={entry.year} placeholder={yearLabel}
        title={yearLabel} style={{ width: 90, flex: '0 0 auto' }}
        onChange={e => onChange(i, { ...entry, year: e.target.value })} />
      <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '4px 8px', color: 'var(--sms-btn-outline-danger-txt)' }}
        onClick={() => onRemove(i)}>
        <i className="bi bi-trash" />
      </button>
    </div>
  ));
}

function renderEntryList(raw, yearSuffix) {
  const entries = parseEntryList(raw);
  if (!entries.length) return null;
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {entries.map((entry, i) => (
        <li key={i}>{entry.text}{entry.year !== '' && entry.year != null ? ` (${entry.year}${yearSuffix})` : ''}</li>
      ))}
    </ul>
  );
}

export function TrainerDirectoryPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [viewing, setViewing] = useState(null);
  // Drawer visibility, separate from `viewing` — `viewing` is never nulled on
  // close (only replaced by the next row opened), so DetailPanel still has
  // data to render while it plays its slide-out animation.
  const [viewingOpen, setViewingOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [openSections, setOpenSections] = useState({ claims: false, assessments: false, observations: false });
  const toggleSection = key => setOpenSections(s => ({ ...s, [key]: !s[key] }));

  const [editingTrainer, setEditingTrainer] = useState(null); // trainer id being edited, or null
  const [editForm, setEditForm] = useState(TRAINER_EDIT_EMPTY);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const attachRef = useRef();

  useEffect(() => {
    setLoading(true);
    api.get('/trainers').then(r => setRows(r.data.data || []))
      .catch(() => toast('Failed to load trainers', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const deptOptions = [...new Set(rows.map(t => t.dept_name).filter(Boolean))].sort().map(d => ({ value: d, label: d }));
  const isActive = trainer => Number(trainer.is_active) === 1;
  const filtered = rows.filter(t =>
    ((t.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
     (t.email || '').toLowerCase().includes(search.toLowerCase())) &&
    (!deptFilter || t.dept_name === deptFilter) &&
    (!statusFilter || (statusFilter === 'active' ? isActive(t) : !isActive(t)))
  );
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);
  const clearFilters = () => { setSearch(''); setDeptFilter(''); setStatusFilter(''); };

  // One pagination instance per detail-modal section, small default page
  // size since these render inside a modal rather than a full page.
  const claimsPg       = usePagination(viewing?.claims || [], 5);
  const assessmentsPg  = usePagination(viewing?.assessments || [], 5);
  const observationsPg = usePagination(viewing?.observations || [], 5);

  const openTrainer = row => {
    setDetailLoading(true);
    setViewing({ profile: row });
    setViewingOpen(true);
    setOpenSections({ claims: false, assessments: false, observations: false });
    claimsPg.setPage(1); assessmentsPg.setPage(1); observationsPg.setPage(1);
    api.get(`/trainers/${row.id}`).then(r => setViewing(r.data.data))
      .catch(() => toast('Failed to load trainer detail', 'error'))
      .finally(() => setDetailLoading(false));
  };

  const openEditTrainer = profile => {
    setEditingTrainer(profile.id);
    setEditForm({
      licence_expiry: profile.licence_expiry ? profile.licence_expiry.slice(0, 10) : '',
      bank_name: profile.bank_name || '',
      bank_account_number: profile.bank_account_number || '',
      qualifications: parseEntryList(profile.qualifications),
      experience: parseEntryList(profile.experience),
    });
    setEditError('');
  };

  const addEntry    = field => setEditForm(f => ({ ...f, [field]: [...f[field], { text: '', year: '' }] }));
  const removeEntry  = (field, idx) => setEditForm(f => ({ ...f, [field]: f[field].filter((_, i) => i !== idx) }));
  const updateEntry  = (field, idx, entry) => setEditForm(f => ({ ...f, [field]: f[field].map((v, i) => i === idx ? entry : v) }));

  const cleanEntries = entries => entries
    .map(e => ({ text: e.text.trim(), year: e.year === '' ? null : Number(e.year) }))
    .filter(e => e.text);

  const handleSaveTrainer = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/trainers/${editingTrainer}`, {
        licence_expiry: editForm.licence_expiry,
        bank_name: editForm.bank_name,
        bank_account_number: editForm.bank_account_number,
        qualifications: cleanEntries(editForm.qualifications),
        experience: cleanEntries(editForm.experience),
      });
      await attachRef.current?.flush(editingTrainer);
      toast('Trainer profile updated', 'success');
      setEditingTrainer(null);
      const r = await api.get(`/trainers/${viewing.profile.id}`);
      setViewing(r.data.data);
      setRows(rs => rs.map(t => t.id === r.data.data.profile.id ? { ...t, ...r.data.data.profile } : t));
    } catch (err) { setEditError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Trainer Profiles" subtitle={`${total} trainers`} />
      <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search name or email…" onClear={clearFilters}>
        <FilterSelect value={deptFilter} onChange={setDeptFilter} placeholder="All departments" options={deptOptions} />
        <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="All statuses"
          options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
      </FilterBar>
      <div className="sms-split-row">
      <div className="sms-card sms-split-table" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? <EmptyState icon="bi-person-badge" message="No trainers found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{ width: 40 }}>#</th>
                <SortTh label="Name" field="full_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Email" field="email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Department</th>
                <SortTh label="Status" field="is_active" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Pending Claims</th>
                <SortTh label="Last Login" field="last_login" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={7} /> : paged.map((t, idx) => (
                  <tr key={t.id} onClick={() => openTrainer(t)} style={{ cursor: 'pointer' }}
                    className={viewingOpen && viewing?.profile?.id===t.id?'sms-row-active':''}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{t.full_name}</td>
                    <td>{t.email}</td>
                    <td>{t.dept_name || '—'}</td>
                    <td><span className={`badge rounded-pill ${isActive(t) ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{isActive(t) ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      {t.pending_claims > 0
                        ? <span className="badge rounded-pill badge-late" style={{ fontSize: 12 }}>{t.pending_claims} pending</span>
                        : <span style={{ color: 'var(--sms-txt3)', fontSize: 12 }}>—</span>}
                    </td>
                    <td>{t.last_login ? fmtDateTime(t.last_login) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
          </>
        )}
      </div>

      {viewing && (
      <DetailPanel show={viewingOpen} onClose={() => setViewingOpen(false)} title="Trainer Detail"
        header={can('trainer_directory','update') && (
          <div className="d-flex justify-content-end">
            <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '4px 10px' }}
              onClick={() => openEditTrainer(viewing.profile)}>
              <i className="bi bi-pencil" /> Edit
            </button>
          </div>
        )}
        fields={[
        { label: 'Name',           value: viewing.profile.full_name },
        { label: 'Email',          value: viewing.profile.email },
        { label: 'Phone',          value: viewing.profile.phone },
        { label: 'NRIC',           value: viewing.profile.nric_no },
        { label: 'Bank Name', value: viewing.profile.bank_name },
        { label: 'Bank Account Number', value: viewing.profile.bank_account_number },
        { label: 'Department',     value: viewing.profile.dept_name },
        { label: 'Status',         value: <span className={`badge rounded-pill ${isActive(viewing.profile) ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{isActive(viewing.profile) ? 'Active' : 'Inactive'}</span> },
        { label: 'Last Login',     value: viewing.profile.last_login ? fmtDateTime(viewing.profile.last_login) : '—' },
        { label: 'Licence Expiry', value: viewing.profile.licence_expiry ? fmtDate(viewing.profile.licence_expiry) : null },
        { label: 'Qualifications', value: renderEntryList(viewing.profile.qualifications, ''), full: true },
        { label: 'Experience',                 value: renderEntryList(viewing.profile.experience, ' yrs'), full: true },
      ]}>
        <div style={{ marginTop: 8 }}>
          <FileAttachments module="trainer_directory" recordId={viewing.profile.id} canUpload={false} canDelete={false} />
        </div>
        {(detailLoading || viewing?.claims) && (
          <div style={{ marginTop: 8 }}>
            <Section sectionKey="claims" label={`Claims (${viewing?.claims?.length ?? 0})`} open={openSections.claims} onToggle={() => toggleSection('claims')}>
              {!detailLoading && viewing.claims.length === 0 ? <EmptyState icon="bi-receipt" message="No claims" /> : (
                <>
                  <table className="sms-table" style={{ marginBottom: 8 }}>
                    <thead><tr><th>Date</th><th className="num">Amount</th><th>Status</th></tr></thead>
                    <tbody>
                      {detailLoading ? <TableSkeletonRows columns={3} /> : claimsPg.paged.map(c => (
                        <tr key={c.id}>
                          <td>{fmtDate(c.claim_date)}</td>
                          <td className="num">{claimAmountLabel(c)}</td>
                          <td><Badge value={c.status} map={claimStatusBadgeMap} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!detailLoading && <Pagination idSuffix="-claims" page={claimsPg.page} totalPages={claimsPg.totalPages} total={claimsPg.total}
                    pageSize={claimsPg.pageSize} setPage={claimsPg.setPage} setPageSize={claimsPg.setPageSize} start={claimsPg.start} />}
                </>
              )}
            </Section>

            <Section sectionKey="assessments" label={`Assessments (${viewing?.assessments?.length ?? 0})`} open={openSections.assessments} onToggle={() => toggleSection('assessments')}>
              {!detailLoading && viewing.assessments.length === 0 ? <EmptyState icon="bi-journal-check" message="No assessments" /> : (
                <>
                  <table className="sms-table" style={{ marginBottom: 8 }}>
                    <thead><tr><th>Module</th><th>Type</th><th>Status</th></tr></thead>
                    <tbody>
                      {detailLoading ? <TableSkeletonRows columns={3} /> : assessmentsPg.paged.map(a => (
                        <tr key={a.id}>
                          <td>{a.module_name || a.module_code || '—'}</td>
                          <td style={{ textTransform: 'capitalize' }}>{a.assessment_type}</td>
                          <td><Badge value={a.status} map={assessmentStatusBadgeMap} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!detailLoading && <Pagination idSuffix="-assessments" page={assessmentsPg.page} totalPages={assessmentsPg.totalPages} total={assessmentsPg.total}
                    pageSize={assessmentsPg.pageSize} setPage={assessmentsPg.setPage} setPageSize={assessmentsPg.setPageSize} start={assessmentsPg.start} />}
                </>
              )}
            </Section>

            <Section sectionKey="observations" label={`Observations (${viewing?.observations?.length ?? 0})`} open={openSections.observations} onToggle={() => toggleSection('observations')}>
              {!detailLoading && viewing.observations.length === 0 ? <EmptyState icon="bi-eye" message="No observations" /> : (
                <>
                  <table className="sms-table" style={{ marginBottom: 8 }}>
                    <thead><tr><th>Date</th><th>Observed By</th><th>Rating</th></tr></thead>
                    <tbody>
                      {detailLoading ? <TableSkeletonRows columns={3} /> : observationsPg.paged.map(o => (
                        <tr key={o.id}>
                          <td>{fmtDate(o.observation_date)}</td>
                          <td>{o.observed_by_name}</td>
                          <td><Badge value={o.rating} map={observationRatingBadgeMap} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!detailLoading && <Pagination idSuffix="-observations" page={observationsPg.page} totalPages={observationsPg.totalPages} total={observationsPg.total}
                    pageSize={observationsPg.pageSize} setPage={observationsPg.setPage} setPageSize={observationsPg.setPageSize} start={observationsPg.start} />}
                </>
              )}
            </Section>
          </div>
        )}
      </DetailPanel>
      )}
      </div>

      <Modal show={!!editingTrainer} onClose={() => setEditingTrainer(null)} title="Edit Trainer Profile">
        <form onSubmit={handleSaveTrainer} noValidate>
          {editError && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{editError}</div>}
          <div className="d-flex flex-column" style={{ gap: 12 }}>
            <FormCard icon="bi-bank" color="var(--sms-stat-blue)" title="Claim payment details">
              <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>NRIC</label>
              <div className="sms-form-control" style={{ display: 'flex', alignItems: 'center', color: 'var(--sms-txt2)', background: 'var(--sms-sur2)' }}>
                {viewing?.profile?.nric_no || 'Sourced from the staff directory'}
              </div>
              <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', margin: '10px 0 5px' }}>Bank Name</label>
              <input type="text" autoComplete="off" className="sms-form-control" maxLength={100}
                value={editForm.bank_name}
                onChange={e => setEditForm(f => ({ ...f, bank_name: e.target.value }))}
                placeholder="e.g. DBS, OCBC, UOB" />
              <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', margin: '10px 0 5px' }}>Bank Account Number</label>
              <input type="text" inputMode="numeric" autoComplete="off" className="sms-form-control"
                value={editForm.bank_account_number}
                onChange={e => setEditForm(f => ({ ...f, bank_account_number: e.target.value }))}
                placeholder="Enter bank account number" />
            </FormCard>
            <FormCard icon="bi-patch-check" color="var(--sms-acc)" title="Licence">
              <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Licence expiry</label>
              <input type="date" className="sms-form-control" value={editForm.licence_expiry}
                onChange={e => setEditForm(f => ({ ...f, licence_expiry: e.target.value }))} />
            </FormCard>

            <FormCard icon="bi-mortarboard" color="var(--sms-stat-green)" title="Qualifications"
              action={<button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => addEntry('qualifications')}><i className="bi bi-plus-lg" /> Add</button>}>
              <EntryListField placeholder="e.g. ACTA Certificate" yearLabel="Year"
                entries={editForm.qualifications}
                onChange={(idx, v) => updateEntry('qualifications', idx, v)}
                onRemove={idx => removeEntry('qualifications', idx)} />
            </FormCard>

            <FormCard icon="bi-briefcase" color="var(--sms-stat-amber)" title="Experience"
              action={<button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => addEntry('experience')}><i className="bi bi-plus-lg" /> Add</button>}>
              <EntryListField placeholder="e.g. XYZ Institute" yearLabel="Years"
                entries={editForm.experience}
                onChange={(idx, v) => updateEntry('experience', idx, v)}
                onRemove={idx => removeEntry('experience', idx)} />
            </FormCard>

            <div className="sms-card" style={{ padding: 14 }}>
              <FileAttachments ref={attachRef} module="trainer_directory" recordId={editingTrainer}
                canUpload={can('trainer_directory','update')} canDelete={can('trainer_directory','update')} />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setEditingTrainer(null)}>Cancel</button>
            <button type="submit" className="sms-btn sms-btn-primary" disabled={saving}>
              {saving ? <><InlineSpinner style={{ marginRight: 4 }} />Saving…</> : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// ─── CLAIMS QUEUE ───────────────────────────────────────────────────────────────
const CLAIM_STATUS_OPTIONS =['under_review', 'verified', 'approved', 'rejected', 'paid', 'cancelled']
  .map(s => ({ value: s, label: claimStatusBadgeMap[s]?.label || s }));

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

// clock_in_time/clock_out_time come back as full "YYYY-MM-DD HH:MM:SS" —
// only the time is useful here since the date is already shown at the top.
function fmtClock(value) {
  if (!value) return null;
  const timePart = String(value).includes(' ') ? String(value).split(' ')[1] : value;
  const [h, m] = timePart.split(':').map(Number);
  if (Number.isNaN(h)) return null;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function fmtSessionTime(t) {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

const CLAIM_STAT_TILES = [
  { key: 'total',   label: 'Total',   icon: 'bi-people',        badge: 'badge-active' },
  { key: 'present', label: 'Present', icon: 'bi-check-circle',  badge: 'badge-present' },
  { key: 'late',    label: 'Late',    icon: 'bi-clock-history', badge: 'badge-late' },
  { key: 'absent',  label: 'Absent',  icon: 'bi-x-circle',      badge: 'badge-absent' },
  { key: 'mc',      label: 'MC',      icon: 'bi-file-medical',  badge: 'badge-mc' },
];

// One session's attendance block within the claim detail — a bulk-submitted
// claim covers several sessions, so the modal renders one of these per item
// instead of a single roster.
function ClaimSessionRoster({ item, roster, striped }) {
  const { sorted, sortKey, sortDir, toggleSort } = useSort(roster?.students || [], 'name', 'asc');
  const session = roster?.session;
  const counts = roster?.counts || { total: 0, present: 0, late: 0, absent: 0, mc: 0 };
  // Names stay hidden behind a second click — the stat counts (Present:2,
  // Absent:1, ...) already answer "did this session run OK" without
  // exposing the roster, so admin only opens it when they need to check
  // a specific student.
  const [showRoster, setShowRoster] = useState(false);

  return (
    <div style={{
      border: '1px solid var(--sms-bdr)', borderRadius: 10, padding: '12px 14px', marginBottom: 12,
      background: striped ? 'var(--sms-sur2)' : 'transparent',
    }}>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {item.module_name || item.module_code}
          <span style={{ fontWeight: 400, color: 'var(--sms-txt2)', marginLeft: 8, fontSize: 12 }}>
            <i className="bi bi-calendar3" /> {fmtDate(item.session_date)}
            {session && <> · <i className="bi bi-clock" /> {fmtSessionTime(session.start_time)}–{fmtSessionTime(session.end_time)}</>}
            {session?.venue_name && <> · <i className="bi bi-geo-alt" /> {session.venue_name}</>}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{fmtHours(item.hours, { unit: true })} · {fmtMoney(item.amount)}</span>
          {session && <Badge value={session.status} map={sessionStatusBadgeMap} />}
        </div>
      </div>

      {!session ? (
        <EmptyState icon="bi-calendar-x" message="Session no longer available" />
      ) : (
        <>
          <div role="button" tabIndex={0}
            className="d-flex align-items-center justify-content-between flex-wrap"
            style={{ cursor: 'pointer', userSelect: 'none', gap: 8 }}
            onClick={() => setShowRoster(v => !v)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setShowRoster(v => !v); }}>
            <div className="d-flex gap-2 flex-wrap">
              {CLAIM_STAT_TILES.map(t => (
                <span key={t.key} className={`badge rounded-pill ${t.badge}`} style={{ fontSize: 12, padding: '4px 8px' }}>
                  <i className={`bi ${t.icon}`} style={{ marginRight: 4 }} /> {t.label}: {counts[t.key]}
                </span>
              ))}
            </div>
            <span style={{
              fontSize: 12, fontWeight: 500, color: 'var(--sms-acc)', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
              paddingLeft: 12, borderLeft: '1px solid var(--sms-bdr)',
            }}>
              {showRoster ? 'Hide attendance details' : 'Attendance details'} <i className={`bi ${showRoster ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
            </span>
          </div>

          {showRoster && (sorted.length === 0 ? (
            <EmptyState icon="bi-people" message="No students recorded for this session" />
          ) : (
            <div style={{ marginTop: 10, maxHeight: 240, overflowY: 'auto', border: '1px solid var(--sms-bdr)', borderRadius: 8 }}>
              <table className="sms-table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <SortTh label="Student" field="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Status" field="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Clock In" field="clock_in_time" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Clock Out" field="clock_out_time" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(s => (
                    <tr key={s.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span style={{
                            width: 24, height: 24, borderRadius: '50%', background: 'var(--sms-sur2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700, color: 'var(--sms-txt2)', flexShrink: 0,
                          }}>{initials(s.name)}</span>
                          {s.name || '—'}
                        </div>
                      </td>
                      <td><Badge value={s.status} map={attendanceBadgeMap} /></td>
                      <td style={{ color: fmtClock(s.clock_in_time) ? 'var(--sms-txt)' : 'var(--sms-txt3)' }}>
                        {fmtClock(s.clock_in_time) || '—'}
                      </td>
                      <td style={{ color: fmtClock(s.clock_out_time) ? 'var(--sms-txt)' : 'var(--sms-txt3)' }}>
                        {fmtClock(s.clock_out_time) || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// Detail view for a single claim record: header summary (a bulk submit is
// ONE claim covering several sessions, so hours/amount here are totals) plus
// one attendance block per session it covers, so admin can verify what
// actually happened before deciding.
function ClaimDetailModal({ claimId, onClose, onSetAmount }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [data, setData] = useState(null); // { claim, rosters: [{item_id, session_id, roster}] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [printing, setPrinting] = useState(false);
  const [showSessions, setShowSessions] = useState(false);

  // Opens the PDF in a new tab (browser's own viewer) so admin can preview
  // it before deciding to save — not an immediate forced download.
  const printClaim = async () => {
    setPrinting(true);
    try {
      const res = await api.get(`/trainers/claims/${claimId}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch {
      toast('Failed to generate PDF', 'error');
    } finally {
      setPrinting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get(`/trainers/claims/${claimId}`)
      .then(r => { if (!cancelled) setData(r.data.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.message || 'Failed to load claim'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [claimId]);

  const claim = data?.claim;
  const items = claim?.items || [];
  const rosterByItemId = Object.fromEntries((data?.rosters || []).map(r => [r.item_id, r.roster]));
  const isBundle = items.length > 1;
  const isAmountPending = claimAmountPending(claim);
  const isManualClaim = isManualClaimType(claim?.fee_type);
  const manualTime = isManualClaim ? claimTimeRange(claim) : '—';
  const manualTimePending = manualTime === '—';
  const manualRate = isManualClaim && claimRateLabel(claim) === 'Not set' ? 'Awaiting rate' : claimRateLabel(claim);

  return (
      <Modal show onClose={onClose} size="lg" title={
      claim ? (
        <span className="d-flex align-items-center gap-2">
          <TrainerAvatar name={claim.trainer_name} avatarUrl={claim.avatar_url} size={30} />
          {claim.trainer_name}
        </span>
      ) : 'Claim'
    }>
      {loading ? <Spinner minHeight="200px" /> : error ? (
        <EmptyState icon="bi-exclamation-triangle" message={error} />
      ) : (
        <>
          <div style={{
            background: 'var(--sms-sur2)', border: '1px solid var(--sms-bdr)',
            borderRadius: 10, padding: '14px 16px', marginBottom: 16,
          }}>
            <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>
                  {isBundle ? `${items.length} sessions` : (isManualClaim ? CLAIM_FEE_TYPE_MAP[claimFeeTypeKey(claim)]?.label : (claim.module_name || claim.module_code || '—'))}
                </div>
                <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                  {isManualClaim ? (
                    <><i className="bi bi-calendar3" aria-hidden="true" /> {fmtDate(claim.claim_date)}</>
                  ) : isBundle ? (
                    <>{[...new Set(items.map(it => it.module_name || it.module_code))].join(', ')}</>
                  ) : (
                    <><ClaimFeeType claim={claim} /><span style={{ margin: '0 6px', color: 'var(--sms-txt3)' }}>·</span><i className="bi bi-mortarboard" /> {claim.intake_code || '—'}</>
                  )}
                  {!isManualClaim && <><span style={{ margin: '0 6px', color: 'var(--sms-txt3)' }}>·</span><i className="bi bi-calendar3" /> {fmtDate(claim.claim_date)}</>}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Badge value={claim.status} map={claimStatusBadgeMap} />
                <button type="button" className="sms-btn sms-btn-outline-info" style={{ fontSize: 12, padding: '4px 8px' }}
                  disabled={printing} onClick={printClaim}>
                  {printing ? <InlineSpinner /> : <><i className="bi bi-printer" /> Print</>}
                </button>
              </div>
            </div>

            {isAmountPending && (
              <div role="alert" style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                marginTop: 12, padding: '9px 11px', borderRadius: 8,
                background: 'color-mix(in srgb, var(--sms-stat-amber) 12%, var(--sms-sur))',
                border: '1px solid color-mix(in srgb, var(--sms-stat-amber) 35%, var(--sms-bdr))',
                color: 'var(--sms-txt2)', fontSize: 12,
              }}>
                <i className="bi bi-exclamation-triangle" style={{ color: 'var(--sms-stat-amber)' }} />
                <span style={{ flex: '1 1 220px' }}>
                  {isManualClaim
                    ? 'Set the payable amount before this Course Preview, Interview or Assessment claim is verified.'
                    : 'Resolve the fee rate and amount before this attendance claim is verified.'}
                </span>
                {isManualClaim && claim?.status === 'under_review' && onSetAmount && (
                  <button type="button" className="sms-btn sms-btn-claim-set-amount" style={{ padding: '4px 8px', fontSize: 12 }}
                    onClick={() => onSetAmount(claim)}>
                    <i className="bi bi-currency-dollar" /> Set amount
                  </button>
                )}
                {!isManualClaim && can('trainer_fee_rates', 'view') && (
                  <button type="button" className="sms-btn sms-btn-outline-info" style={{ padding: '4px 8px', fontSize: 12 }}
                    onClick={() => navigate('/trainers/fee-rates', feeRateSetupNavigation(claim))}>
                    <i className="bi bi-arrow-up-right" /> Open Fee Rates
                  </button>
                )}
              </div>
            )}

            <div className="d-flex align-items-center gap-4 flex-wrap" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--sms-bdr)' }}>
              {(!isManualClaim || claim?.hours != null) && <div>
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{isManualClaim ? 'Duration' : 'Hours'}</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{fmtHours(claim.hours)}</div>
              </div>}
              {isManualClaim && !manualTimePending && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--sms-txt3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Time</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{manualTime}</div>
                </div>
              )}
              {isManualClaim && claim?.pax != null && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--sms-txt3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Pax</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{claim.pax}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Rate</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{isBundle ? 'Multiple' : (isManualClaim ? manualRate : claimRateLabel(claim))}</div>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Amount</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--sms-acc)' }}>{claimAmountLabel(claim)}</div>
              </div>
            </div>
          </div>

          {claim.remarks && (
            <div style={{
              fontSize: 12, color: 'var(--sms-txt2)', background: 'var(--sms-sur2)',
              border: '1px solid var(--sms-bdr)', padding: '8px 12px', borderRadius: 8, marginBottom: 16,
            }}>
              <i className="bi bi-chat-left-text" style={{ marginRight: 6 }} /> {claim.remarks}
            </div>
          )}

          {(data?.signoffs || []).length > 0 && (
            <div style={{
              fontSize: 12, color: 'var(--sms-txt2)', background: 'var(--sms-sur2)',
              border: '1px solid var(--sms-bdr)', padding: '10px 12px', borderRadius: 8, marginBottom: 16,
            }}>
              <div style={{ fontWeight: 600, color: 'var(--sms-txt)', marginBottom: 6 }}><i className="bi bi-pen" style={{ marginRight: 6 }} />Signatures</div>
              {data.signoffs.map(sg => (
                <div key={sg.role} className="d-flex justify-content-between gap-3 flex-wrap" style={{ padding: '2px 0' }}>
                  <span>{sg.label}</span>
                  <span style={{ color: 'var(--sms-txt)' }}>{sg.name || '—'} · {fmtDateTime(sg.signed_at)}</span>
                </div>
              ))}
            </div>
          )}

          {!isManualClaim && (
            <Section sectionKey="claims" label={`Session Attendance (${items.length})`}
              open={showSessions} onToggle={() => setShowSessions(v => !v)}>
              {items.length === 0 ? (
                <EmptyState icon="bi-calendar-x" message="No session linked to this claim" />
              ) : (
                items.map((item, idx) => (
                  <ClaimSessionRoster key={item.id} item={item} roster={rosterByItemId[item.id]} striped={idx % 2 === 1} />
                ))
              )}
            </Section>
          )}
        </>
      )}
    </Modal>
  );
}

export function TrainerClaimsQueuePage() {
  const toast = useToast();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [amountTarget, setAmountTarget] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [pax, setPax] = useState('');
  const [amountError, setAmountError] = useState('');
  const [amountSaving, setAmountSaving] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [filter, setFilter] = useState(location.state?.filter ?? 'under_review');
  const autoFilterApplied = useRef(!!location.state?.filter);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [decideTarget, setDecideTarget] = useState(null); // { claim, status } — reject or mark paid
  const [signoffTarget, setSignoffTarget] = useState(null); // { claim, mode: 'verify' | 'approve' }
  const [isApprover, setIsApprover] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewingClaimId, setViewingClaimId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [claimsOpenDay, setClaimsOpenDay] = useState(1);
  const [claimsDueDays, setClaimsDueDays] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [summary, setSummary] = useState({
    underReviewCount: 0, underReviewAmount: 0,
    verifiedCount: 0, verifiedAmount: 0,
    approvedCount: 0, approvedAmount: 0,
    paidThisYearCount: 0, paidThisYearAmount: 0,
  });

  const load = () => {
    setLoading(true);
    api.get('/trainers/claims', { params: filter ? { status: filter } : {} })
      .then(r => setRows(r.data.data || []))
      .catch(() => toast('Failed to load claims', 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  // Independent of the (possibly status-filtered) table above — always
  // pulled from every claim, not just whatever `filter` currently narrows
  // the table to, so these numbers stay correct no matter what's selected.
  const loadSummary = () => {
    api.get('/trainers/claims').then(r => {
      const all = r.data.data || [];
      const thisYear = String(new Date().getFullYear());
      const underReview = all.filter(c => c.status === 'under_review');
      const verified = all.filter(c => c.status === 'verified');
      const approved = all.filter(c => c.status === 'approved');
      const paidThisYear = all.filter(c => c.status === 'paid' && (c.decided_at || c.claim_date || '').slice(0, 4) === thisYear);
      setSummary({
        underReviewCount: underReview.length,
        underReviewAmount: underReview.reduce((sum, c) => sum + Number(c.amount), 0),
        verifiedCount: verified.length,
        verifiedAmount: verified.reduce((sum, c) => sum + Number(c.amount), 0),
        approvedCount: approved.length,
        approvedAmount: approved.reduce((sum, c) => sum + Number(c.amount), 0),
        paidThisYearCount: paidThisYear.length,
        paidThisYearAmount: paidThisYear.reduce((sum, c) => sum + Number(c.amount), 0),
      });
    }).catch(() => {});
  };
  useEffect(() => { loadSummary(); }, []);
  // The approver lands on what needs their signature (once, unless they came here with a filter).
  useEffect(() => {
    if (!isApprover || autoFilterApplied.current || !summary.verifiedCount) return;
    autoFilterApplied.current = true;
    setFilter('verified');
  }, [isApprover, summary.verifiedCount]);
  // Only the TAEPP approver sees the Approve button; the server enforces it too.
  useEffect(() => {
    if (!can('trainer_claims_queue', 'update')) return;
    api.get('/trainers/claims/signoff-info').then(r => setIsApprover(!!r.data.data.is_approver)).catch(() => {});
  }, []);

  const openSettings = () => {
    api.get('/trainers/claims/settings')
      .then(r => {
        setClaimsOpenDay(r.data.data.claims_open_day);
        setClaimsDueDays(r.data.data.claims_due_days || '');
      })
      .catch(() => toast('Failed to load claims window setting', 'error'));
    setSettingsOpen(true);
  };
  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await api.put('/trainers/claims/settings', { claims_open_day: claimsOpenDay, claims_due_days: claimsDueDays || null });
      toast('Claims window updated', 'success');
      setSettingsOpen(false);
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to update claims window', 'error');
    } finally {
      setSettingsSaving(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = rows.filter(c =>
    (!q || (c.trainer_name || '').toLowerCase().includes(q)) &&
    (!dateFrom || c.claim_date >= dateFrom) &&
    (!dateTo || c.claim_date <= dateTo)
  );
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);

  const clearFilters = () => { setFilter(''); setSearch(''); setDateFrom(''); setDateTo(''); };

  const openDecide = (claim, status) => { setDecideTarget({ claim, status }); setRemarks(claim.remarks || ''); };
  const openSignoff = (claim, mode) => setSignoffTarget({ claim, mode });

  const openAmountEditor = claim => {
    setAmountTarget(claim);
    setStartTime(claim.start_time ? String(claim.start_time).slice(0, 5) : '');
    setEndTime(claim.end_time ? String(claim.end_time).slice(0, 5) : '');
    setPax(claim.pax ?? '');
    setAmountError('');
  };

  const handleAmountSave = async e => {
    e.preventDefault();
    const duration = claimDurationHours(startTime, endTime);
    if (!startTime || !endTime) {
      setAmountError('Select both a start time and an end time.');
      return;
    }
    if (duration === null) {
      setAmountError('End time must be later than start time.');
      return;
    }
    const needsPax = isPerPaxClaim(amountTarget);
    if (needsPax && pax === '') {
      setAmountError(`Enter the number of pax for this ${amountTarget.fee_type === 'interview' ? 'Interview' : 'Assessment F.A. 1.2.'} claim.`);
      return;
    }
    if (needsPax && (!Number.isInteger(Number(pax)) || Number(pax) <= 0)) {
      setAmountError('No. of pax must be a positive whole number.');
      return;
    }
    setAmountSaving(true);
    setAmountError('');
    try {
      await api.put(`/trainers/claims/${amountTarget.id}/amount`, {
        start_time: startTime,
        end_time: endTime,
        pax: needsPax ? Number(pax) : null,
      });
      toast('Claim time and amount updated', 'success');
      setAmountTarget(null);
      load();
      loadSummary();
    } catch (err) {
      setAmountError(err.response?.data?.message || 'Failed to update claim amount');
    } finally {
      setAmountSaving(false);
    }
  };

  const printClaim = async claimId => {
    setPrintingId(claimId);
    try {
      const res = await api.get(`/trainers/claims/${claimId}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch {
      toast('Failed to generate PDF', 'error');
    } finally {
      setPrintingId(null);
    }
  };

  const handleDecide = async () => {
    setSaving(true);
    try {
      await api.put(`/trainers/claims/${decideTarget.claim.id}/decide`, { status: decideTarget.status, remarks });
      toast(decideTarget.status === 'rejected' ? 'Claim rejected' : 'Claim marked paid', 'success');
      setDecideTarget(null);
      load();
      loadSummary();
    } catch (err) {
      toast(err.response?.data?.message || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const durationPreview = claimDurationHours(startTime, endTime);
  const timeRangeError = startTime && endTime && durationPreview === null
    ? 'End time must be later than start time.'
    : '';
  const ratePreview = claimRateValue(amountTarget);
  const hasSavedRate = claimHasSavedRate(amountTarget);
  const rateCoverageText = amountTarget?.available_rate_start_date && amountTarget?.available_rate_end_date
    ? `Effective ${fmtDate(amountTarget.available_rate_start_date)} to ${fmtDate(amountTarget.available_rate_end_date)}`
    : hasSavedRate
      ? 'Saved with this claim at submission; termination does not change it.'
      : 'Matched to the claim date.';
  const perPaxTarget = isPerPaxClaim(amountTarget);
  const paxNumber = pax !== '' && Number.isInteger(Number(pax)) && Number(pax) > 0 ? Number(pax) : null;
  const billableUnits = perPaxTarget ? paxNumber : durationPreview;
  const amountPreview = billableUnits !== null && Number.isFinite(ratePreview) && ratePreview > 0
    ? Number((billableUnits * ratePreview).toFixed(2))
    : null;

  return (
    <>
      <PageHeader title="Trainer Claims Queue" subtitle={`${summary.underReviewCount} pending verification · ${summary.verifiedCount} pending approval`}
        actions={can('trainer_claims_queue', 'update') && (
          <button type="button" className="sms-btn sms-btn-outline-info" onClick={openSettings}>
            <i className="bi bi-gear" /> Claims Window
          </button>
        )} />

      {isApprover && summary.verifiedCount > 0 && (
        <div className="sms-approval-banner" role="alert">
          <i className="bi bi-patch-exclamation" aria-hidden="true" />
          <div className="sms-approval-banner-text">
            <strong>{summary.verifiedCount} claim{summary.verifiedCount === 1 ? ' is' : 's are'} waiting for your signature</strong>
            <span>{filter === 'verified'
              ? 'Click Sign to Approve on a claim to review it and sign, or use the × to reject it.'
              : 'They have been verified. Show them, then click Sign to Approve on each one.'}</span>
          </div>
          {filter !== 'verified' && (
            <button type="button" className="sms-btn sms-btn-primary" onClick={() => setFilter('verified')}>
              <i className="bi bi-funnel" aria-hidden="true" /> Show Them
            </button>
          )}
        </div>
      )}

      <div className="row g-3 mb-3 sms-stat-anim-row">
        <div className="col-md-3">
          <ClaimStatCard label="Pending Verification" value={summary.underReviewCount} icon="bi-hourglass-split" color="var(--sms-stat-amber)"
            sub={summary.underReviewCount ? `${fmtMoney(summary.underReviewAmount)} to verify` : 'Nothing waiting'}
            active={filter === 'under_review'} onClick={() => setFilter('under_review')} />
        </div>
        <div className="col-md-3">
          <ClaimStatCard label="Pending Approval" value={summary.verifiedCount} icon="bi-patch-check" color="var(--sms-stat-purple, var(--sms-stat-amber))"
            sub={summary.verifiedCount ? `${fmtMoney(summary.verifiedAmount)} to approve` : 'Nothing waiting'}
            active={filter === 'verified'} onClick={() => setFilter('verified')} />
        </div>
        <div className="col-md-3">
          <ClaimStatCard label="Approved" value={summary.approvedCount} icon="bi-check-circle" color="var(--sms-stat-blue)"
            sub={summary.approvedCount ? `${fmtMoney(summary.approvedAmount)} awaiting payment` : 'Nothing awaiting payment'}
            active={filter === 'approved'} onClick={() => setFilter('approved')} />
        </div>
        <div className="col-md-3">
          <ClaimStatCard label="Paid This Year" value={fmtMoney(summary.paidThisYearAmount)} icon="bi-cash-coin" color="var(--sms-stat-green)"
            sub={`${summary.paidThisYearCount} claim${summary.paidThisYearCount === 1 ? '' : 's'} paid in ${new Date().getFullYear()}`} />
        </div>
      </div>

      <div className="sms-card mb-3" style={{ padding: '12px 14px' }}>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--sms-txt3)', fontSize: 13 }} />
            <input className="sms-form-control" style={{ paddingLeft: 28 }}
              placeholder="Search trainer name…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <SearchableSelect value={filter} onChange={e => setFilter(e.target.value)} placeholder="All statuses" options={CLAIM_STATUS_OPTIONS} />
          </div>
          <div className="d-flex align-items-center gap-1" style={{ flex: '1 1 140px', minWidth: 140 }}>
            <label style={{ fontSize: 12, color: 'var(--sms-txt2)', whiteSpace: 'nowrap' }}>From</label>
            <input type="date" className="sms-form-control" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div className="d-flex align-items-center gap-1" style={{ flex: '1 1 140px', minWidth: 140 }}>
            <label style={{ fontSize: 12, color: 'var(--sms-txt2)', whiteSpace: 'nowrap' }}>To</label>
            <input type="date" className="sms-form-control" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          <button className="sms-btn sms-btn-outline-danger" style={{ flex: '0 0 auto' }} onClick={clearFilters}>
            <i className="bi bi-x-circle" /> Clear
          </button>
        </div>
      </div>

      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? <EmptyState icon="bi-receipt" message="No claims found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{ width: 40 }}>#</th>
                <SortTh label="Trainer" field="trainer_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Claim Type</th>
                <SortTh label="Date" field="claim_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th className="num">Rate/hr</th>
                <th className="num">Amount</th>
                <SortTh label="Status" field="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Submitted" field="submitted_at" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th style={{ width: 220 }}>Actions</th>
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={9} /> : paged.map((c, idx) => (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setViewingClaimId(c.id)}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{c.trainer_name}</td>
                    <td><ClaimFeeType claim={c} /></td>
                    <td>{fmtDate(c.claim_date)}</td>
                    <td className="num">{claimRateLabel(c)}</td>
                    <td className="num">{claimAmountLabel(c)}</td>
                    <td><Badge value={c.status} map={claimStatusBadgeMap} /></td>
                    <td>{c.submitted_at ? fmtDate(c.submitted_at) : '—'}</td>
                    <td onClick={e => e.stopPropagation()}><div className="d-flex gap-1 flex-wrap">
                      <button type="button" className="sms-btn sms-btn-outline-info" style={{ padding: '4px 6px' }} title="Print claim" aria-label="Print claim"
                        disabled={printingId === c.id} onClick={() => printClaim(c.id)}>
                        {printingId === c.id ? <InlineSpinner /> : <i className="bi bi-printer" />}
                      </button>
                      {can('trainer_claims_queue', 'update') && (
                        <>
                          {c.status === 'under_review' && (
                            <>
                              {claimAmountPending(c) ? (
                                isManualClaimType(c.fee_type) ? (
                                  <button className="sms-btn sms-btn-claim-set-amount" style={{ padding: '4px 8px', fontSize: 12 }} title="Set the amount before verifying" onClick={() => openAmountEditor(c)}>
                                    <i className="bi bi-currency-dollar" /> Set amount
                                  </button>
                                ) : (
                                  <button className="sms-btn sms-btn-outline-info" style={{ padding: '4px 8px', fontSize: 12 }} title="Resolve the fee rate before verifying" onClick={() => navigate('/trainers/fee-rates', feeRateSetupNavigation(c))}>
                                    <i className="bi bi-arrow-up-right" /> Open Fee Rates
                                  </button>
                                )
                              ) : (
                                <button className="sms-btn sms-btn-claim-approve" style={{ padding: '4px 8px', fontSize: 12 }} title="Sign to verify this claim" onClick={() => openSignoff(c, 'verify')}><i className="bi bi-patch-check" /> Verify</button>
                              )}
                              <button className="sms-btn sms-btn-outline-danger" style={{ padding: '4px 6px' }} title="Reject" aria-label="Reject" onClick={() => openDecide(c, 'rejected')}><i className="bi bi-x-lg" /></button>
                            </>
                          )}
                          {c.status === 'verified' && (isApprover ? (
                            <>
                              <button className="sms-btn sms-btn-claim-approve" style={{ padding: '4px 8px', fontSize: 12 }} title="Sign to approve this claim" onClick={() => openSignoff(c, 'approve')}><i className="bi bi-pen" /> Sign to Approve</button>
                              <button className="sms-btn sms-btn-outline-danger" style={{ padding: '4px 6px' }} title="Reject" aria-label="Reject" onClick={() => openDecide(c, 'rejected')}><i className="bi bi-x-lg" /></button>
                            </>
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--sms-txt2)', alignSelf: 'center' }}>Waiting for the approver</span>
                          ))}
                          {c.status === 'approved' && (
                            claimAmountPending(c) ? (
                              <button className="sms-btn sms-btn-claim-set-amount" style={{ padding: '4px 8px', fontSize: 12 }} title="Set the amount before marking paid" onClick={() => isManualClaimType(c.fee_type) ? openAmountEditor(c) : navigate('/trainers/fee-rates', feeRateSetupNavigation(c))}>
                                <i className="bi bi-currency-dollar" /> Set amount
                              </button>
                            ) : (
                              <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px', fontSize: 12 }} title="Mark paid" onClick={() => openDecide(c, 'paid')}><i className="bi bi-cash-coin" /> Mark Paid</button>
                            )
                          )}
                        </>
                      )}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
          </>
        )}
      </div>

      <Modal show={!!decideTarget} onClose={() => setDecideTarget(null)} title={decideTarget ? (decideTarget.status === 'rejected' ? 'Reject Claim' : 'Mark Claim as Paid') : ''}>
        {decideTarget && (
          <>
            <p style={{ fontSize: 13, color: 'var(--sms-txt2)' }}>
              {decideTarget.claim.trainer_name} · {fmtDate(decideTarget.claim.claim_date)} · {claimAmountLabel(decideTarget.claim)}
            </p>
            {decideTarget.status === 'rejected' && (
              <p style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>The trainer is emailed the remarks below.</p>
            )}
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Remarks</label>
            <textarea className="sms-form-control" rows={3} value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Optional notes" />
            <div className="d-flex gap-2 justify-content-end mt-3">
              <button type="button" className="sms-btn sms-btn-outline" onClick={() => setDecideTarget(null)}><i className="bi bi-x-lg" /> Cancel</button>
              <button type="button" className="sms-btn sms-btn-primary" disabled={saving} onClick={handleDecide}>
                {saving ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> Confirm</>}
              </button>
            </div>
          </>
        )}
      </Modal>

      <ClaimSignOffModal show={!!signoffTarget} claim={signoffTarget?.claim} mode={signoffTarget?.mode}
        subtitle={signoffTarget ? `${signoffTarget.claim.trainer_name} · ${fmtDate(signoffTarget.claim.claim_date)} · ${claimAmountLabel(signoffTarget.claim)}` : ''}
        onClose={() => setSignoffTarget(null)}
        onChanged={signed => {
          if (signed) { toast(signoffTarget?.mode === 'approve' ? 'Claim approved' : 'Claim verified', 'success'); setSignoffTarget(null); }
          load(); loadSummary();
        }} />

      {viewingClaimId && <ClaimDetailModal claimId={viewingClaimId} onClose={() => setViewingClaimId(null)} onSetAmount={claim => { setViewingClaimId(null); openAmountEditor(claim); }} />}

      <Modal show={!!amountTarget} onClose={() => !amountSaving && setAmountTarget(null)} title={
        <span className="claim-completion-modal-title"><i className="bi bi-calculator" aria-hidden="true" /> Complete Claim Details</span>
      }>
        {amountTarget && (
          <form className="claim-completion-form" onSubmit={handleAmountSave} noValidate>
            <div className="claim-completion-intro">
              <i className="bi bi-info-circle-fill" aria-hidden="true" />
              <span>Select the actual time worked. Hours and payable amount are calculated automatically from the effective rate.</span>
            </div>
            {amountError && <div className="claim-completion-error-banner" role="alert"><i className="bi bi-exclamation-octagon-fill" aria-hidden="true" /> <span>{amountError}</span></div>}
            <div className="claim-completion-identity">
              <TrainerAvatar name={amountTarget.trainer_name} avatarUrl={amountTarget.avatar_url} />
              <div style={{ minWidth: 0 }}>
                <div className="claim-completion-identity-name">{amountTarget.trainer_name}</div>
                <div className="claim-completion-identity-meta">
                  <span><i className="bi bi-calendar3" aria-hidden="true" /> {fmtDate(amountTarget.claim_date)}</span>
                  <span aria-hidden="true">·</span>
                  <span><i className="bi bi-tag" aria-hidden="true" /> {CLAIM_FEE_TYPE_MAP[claimFeeTypeKey(amountTarget)]?.label || 'Manual claim'}</span>
                </div>
              </div>
            </div>
            <div className={`claim-completion-rate ${ratePreview === null ? 'is-missing' : 'is-ready'}`} role={ratePreview === null ? 'alert' : undefined}>
              <div className="claim-completion-rate-icon" aria-hidden="true"><i className={`bi ${ratePreview === null ? 'bi-exclamation-triangle' : 'bi-cash-coin'}`} /></div>
              <div className="claim-completion-rate-body">
                {ratePreview === null ? (
                  <>
                    <div className="claim-completion-rate-title">Rate required</div>
                    <div className="claim-completion-rate-detail">No active fee rate covers {fmtDate(amountTarget.claim_date)}. Set one before saving.</div>
                  </>
                ) : (
                  <>
                    <div className="claim-completion-rate-title">{hasSavedRate ? 'Saved rate' : 'Rate'}/{perPaxTarget ? 'pax' : 'hr'} <strong>{fmtMoney(ratePreview)}</strong></div>
                    <div className="claim-completion-rate-detail">{rateCoverageText}</div>
                  </>
                )}
              </div>
              {ratePreview === null && can('trainer_fee_rates', 'view') && <button type="button" className="sms-btn sms-btn-outline-warning" style={{ padding: '5px 8px', fontSize: 12 }} onClick={() => navigate('/trainers/fee-rates', feeRateSetupNavigation(amountTarget))}>
                <i className="bi bi-arrow-up-right" /> Open Fee Rates
              </button>}
              {ratePreview !== null && <i className="bi bi-check-circle-fill claim-completion-rate-state" aria-label="Rate available" />}
            </div>
            <div className="claim-completion-fields">
              <div className="claim-completion-field" style={{ '--claim-step': 0 }}>
                <label className="claim-completion-field-label" htmlFor="trainer-claim-start-time"><i className="bi bi-clock" aria-hidden="true" /> Start time *</label>
                <input id="trainer-claim-start-time" type="time" className="sms-form-control" value={startTime}
                  max={endTime || undefined}
                  aria-invalid={timeRangeError ? 'true' : 'false'}
                  onChange={e => { setStartTime(e.target.value); setAmountError(''); }} required />
              </div>
              <div className="claim-completion-field" style={{ '--claim-step': 1 }}>
                <label className="claim-completion-field-label" htmlFor="trainer-claim-end-time"><i className="bi bi-clock-history" aria-hidden="true" /> End time *</label>
                <input id="trainer-claim-end-time" type="time" className="sms-form-control" value={endTime}
                  min={startTime || undefined}
                  aria-invalid={timeRangeError ? 'true' : 'false'}
                  onChange={e => { setEndTime(e.target.value); setAmountError(''); }} required />
                {timeRangeError && <div className="claim-completion-error" role="alert"><i className="bi bi-exclamation-circle-fill" aria-hidden="true" /> <span>{timeRangeError}</span></div>}
              </div>
              {/* Pax only matters when the claim is paid per pax (Interview, F.A. 1.2.);
                  Course Preview and duration-based assessments use time alone. */}
              {perPaxTarget && (
                <div className="claim-completion-field" style={{ '--claim-step': 2 }}>
                  <label className="claim-completion-field-label" htmlFor="trainer-claim-pax"><i className="bi bi-people" aria-hidden="true" /> No. of pax *</label>
                  <input id="trainer-claim-pax" type="number" className="sms-form-control claim-completion-input" min="1" step="1" value={pax}
                    onChange={e => { setPax(e.target.value); setAmountError(''); }} placeholder="e.g. 5" />
                </div>
              )}
            </div>
            <div className="claim-completion-calculation">
              <div className="claim-completion-calc-item">
                <span className="claim-completion-calc-label"><i className="bi bi-hourglass-split" aria-hidden="true" /> Duration</span>
                <strong className="claim-completion-calc-value">{durationPreview === null ? '—' : `${fmtHours(durationPreview, { decimals: 2 })} hours`}</strong>
              </div>
              <div className="claim-completion-calc-item">
                <span className="claim-completion-calc-label"><i className="bi bi-wallet2" aria-hidden="true" /> Payable amount</span>
                <strong className={`claim-completion-calc-value ${amountPreview !== null ? 'is-ready' : ''}`}>{amountPreview !== null ? fmtMoney(amountPreview) : '—'}</strong>
              </div>
              <div className="claim-completion-calc-help">
                <i className="bi bi-lightbulb" aria-hidden="true" />
                <span>{durationPreview === null ? 'Choose an end time later than the start time.' : amountPreview !== null ? (perPaxTarget ? 'Calculated from the number of pax multiplied by the effective per-pax rate.' : 'Calculated from duration multiplied by the effective hourly rate.') : 'The payable amount will appear after an effective rate is available.'}</span>
              </div>
            </div>
            <div className="claim-completion-actions d-flex gap-2 justify-content-end mt-4">
              <button type="button" className="sms-btn sms-btn-outline" onClick={() => setAmountTarget(null)} disabled={amountSaving}><i className="bi bi-x-lg" /> Cancel</button>
              <button type="submit" className="sms-btn sms-btn-primary" disabled={amountSaving}>
                {amountSaving ? <InlineSpinner /> : <><i className="bi bi-calculator" /> Calculate and save</>}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal show={settingsOpen} onClose={() => setSettingsOpen(false)} title="Claims Window">
        <p style={{ fontSize: 13, color: 'var(--sms-txt2)', marginBottom: 16 }}>
          Controls when trainers are allowed to submit Attendance claims for teaching hours. A trainer can
          never claim for the month that's still in progress — these two settings decide when
          <em> last month's</em> sessions open for claiming, and how long trainers have to submit them.
          Course Preview and Interview claims are submitted separately and are not controlled by this window.
        </p>

        <div style={{
          background: 'var(--sms-sur2)', border: '1px solid var(--sms-bdr)', borderRadius: 8,
          padding: '10px 12px', marginBottom: 18, fontSize: 12.5, color: 'var(--sms-txt1)', lineHeight: 1.5,
        }}>
          <i className="bi bi-info-circle" style={{ marginRight: 6, color: 'var(--sms-stat-blue)' }} />
          {claimsDueDays
            ? <>On the <b>{ordinal(claimsOpenDay || 1)}</b> of each month, last month's sessions open for claiming.
                Trainers then have <b>{claimsDueDays} day{Number(claimsDueDays) === 1 ? '' : 's'}</b> — until the{' '}
                <b>{claimsCloseLabel(claimsOpenDay || 1, claimsDueDays)}</b> — to submit them before the window closes.</>
            : <>On the <b>{ordinal(claimsOpenDay || 1)}</b> of each month, last month's sessions open for claiming,
                and stay open with <b>no deadline</b> unless you set one below.</>}
        </div>

        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt1)', display: 'block', marginBottom: 3 }}>
          1. Open the window on day
        </label>
        <p style={{ fontSize: 12, color: 'var(--sms-txt3)', marginBottom: 6 }}>
          Day of the month (1–28) that last month's sessions become claimable.
        </p>
        <input type="number" className="sms-form-control" min={1} max={28} style={{ maxWidth: 120 }}
          value={claimsOpenDay} onChange={e => setClaimsOpenDay(e.target.value)} />

        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt1)', display: 'block', marginTop: 18, marginBottom: 3 }}>
          2. Close it again after <span style={{ fontWeight: 400, color: 'var(--sms-txt3)' }}>(optional)</span>
        </label>
        <p style={{ fontSize: 12, color: 'var(--sms-txt3)', marginBottom: 6 }}>
          Give trainers a deadline instead of leaving the window open indefinitely. Leave blank for no deadline.
        </p>
        <div className="d-flex align-items-center gap-2">
          <input type="number" className="sms-form-control" min={1} max={60} style={{ maxWidth: 90 }}
            placeholder="None"
            value={claimsDueDays} onChange={e => setClaimsDueDays(e.target.value)} />
          <span style={{ fontSize: 12.5, color: 'var(--sms-txt2)', whiteSpace: 'nowrap' }}>days after it opens</span>
        </div>

        <div className="d-flex gap-2 justify-content-end mt-4">
          <button type="button" className="sms-btn sms-btn-outline" onClick={() => setSettingsOpen(false)}><i className="bi bi-x-lg" /> Cancel</button>
          <button type="button" className="sms-btn sms-btn-primary" disabled={settingsSaving} onClick={saveSettings}>
            {settingsSaving ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> Save</>}
          </button>
        </div>
      </Modal>
    </>
  );
}

// ─── ASSESSMENTS QUEUE ──────────────────────────────────────────────────────────
const ASSESSMENT_STATUS_OPTIONS = ['draft', 'pending_review', 'returned', 'graded']
  .map(s => ({ value: s, label: assessmentStatusBadgeMap[s]?.label || s }));

export function TrainerAssessmentsQueuePage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows, setRows] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [modules, setModules] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trainerFilter, setTrainerFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [intakeFilter, setIntakeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [decideTarget, setDecideTarget] = useState(null); // { assessment, status }
  const [decideRemarks, setDecideRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null); // assessment row
  const [scores, setScores] = useState([]);
  const [scoresLoading, setScoresLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/trainers/assessments', {
      params: {
        trainer_id: trainerFilter || undefined,
        module_code: moduleFilter || undefined,
        intake_code: intakeFilter || undefined,
        status: statusFilter || undefined,
      },
    })
      .then(r => setRows(r.data.data || []))
      .catch(() => toast('Failed to load assessments', 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [trainerFilter, moduleFilter, intakeFilter, statusFilter]);
  useEffect(() => {
    api.get('/trainers').then(r => setTrainers(r.data.data || [])).catch(() => {});
    // Active modules/intakes only, for the filter bar — same as elsewhere in
    // the Trainers admin area (mirrors getObservationOptions).
    api.get('/trainers/assessments/options').then(r => {
      setModules(r.data.data?.modules || []);
      setIntakes(r.data.data?.intakes || []);
    }).catch(() => {});
  }, []);

  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);

  const pendingCount = rows.filter(r => r.status === 'pending_review').length;
  const clearFilters = () => { setTrainerFilter(''); setModuleFilter(''); setIntakeFilter(''); setStatusFilter(''); };

  const openDecide = (assessment, status) => { setDecideTarget({ assessment, status }); setDecideRemarks(''); };

  const openScores = assessment => {
    setViewing(assessment);
    setScoresLoading(true);
    api.get(`/trainers/assessments/${assessment.id}/scores`)
      .then(r => setScores(r.data.data || []))
      .catch(() => toast('Failed to load student scores', 'error'))
      .finally(() => setScoresLoading(false));
  };

  const handleDecide = async () => {
    if (decideTarget.status === 'returned' && !decideRemarks.trim()) {
      toast('Remarks are required when sending an assessment back', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/trainers/assessments/${decideTarget.assessment.id}/decide`, { status: decideTarget.status, remarks: decideRemarks });
      toast(`Assessment marked ${assessmentStatusBadgeMap[decideTarget.status]?.label || decideTarget.status}`, 'success');
      setDecideTarget(null);
      load();
    } catch (err) {
      toast(err.response?.data?.message || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title="Assessments" subtitle={`${pendingCount} awaiting review`} />

      <div className="sms-card mb-3" style={{ padding: '12px 14px' }}>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div style={{ flex: '1 1 200px', minWidth: 160 }}>
            <SearchableSelect value={trainerFilter} onChange={e => setTrainerFilter(e.target.value)} placeholder="All trainers"
              options={trainers.map(t => ({ value: t.id, label: t.full_name, right: t.email }))} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <SearchableSelect value={intakeFilter} onChange={e => setIntakeFilter(e.target.value)} placeholder="All intakes"
              options={intakes.map(i => ({ value: i.intake_code, label: i.intake_code }))} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <SearchableSelect value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} placeholder="All modules"
              options={modules.map(m => ({ value: m.module_code, label: m.module_name, right: m.module_code }))} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <SearchableSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)} placeholder="All statuses" options={ASSESSMENT_STATUS_OPTIONS} />
          </div>
          <button className="sms-btn sms-btn-outline-danger" style={{ flex: '0 0 auto' }} onClick={clearFilters}>
            <i className="bi bi-x-circle" /> Clear
          </button>
        </div>
      </div>

      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && rows.length === 0 ? <EmptyState icon="bi-journal-check" message="No assessments found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{ width: 40 }}>#</th>
                <SortTh label="Trainer" field="trainer_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Module</th>
                <th>Intake</th>
                <th style={{ textTransform: 'capitalize' }}>Type</th>
                <SortTh label="Due Date" field="due_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th className="num">Submitted</th>
                <SortTh label="Status" field="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                {can('trainer_assessments', 'update') && <th style={{ width: 70 }}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={8 + (can('trainer_assessments', 'update') ? 1 : 0)} /> : paged.map((a, idx) => (
                  <tr key={a.id} onClick={() => openScores(a)} style={{ cursor: 'pointer' }}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{a.trainer_name}</td>
                    <td>{a.module_name || a.module_code || '—'}</td>
                    <td>{a.intake_code || '—'}</td>
                    <td style={{ textTransform: 'capitalize' }}>{a.assessment_type}</td>
                    <td>{a.due_date ? fmtDate(a.due_date) : '—'}</td>
                    <td className="num">{a.submitted_count}/{a.total_students}</td>
                    <td>
                      <Badge value={a.status} map={assessmentStatusBadgeMap} />
                      {a.status === 'returned' && a.remarks && (
                        <i className="bi bi-info-circle" style={{ marginLeft: 6, color: 'var(--sms-txt2)', cursor: 'help' }} title={a.remarks} />
                      )}
                    </td>
                    {can('trainer_assessments', 'update') && (
                      <td onClick={e => e.stopPropagation()}>
                        {a.status === 'pending_review' && (
                          <div className="d-flex gap-1">
                            <button className="sms-btn sms-btn-outline" style={{ padding: '4px 6px' }} title="Confirm Graded" onClick={() => openDecide(a, 'graded')}><i className="bi bi-check-lg" /></button>
                            <button className="sms-btn sms-btn-outline-danger" style={{ padding: '4px 6px' }} title="Send Back" onClick={() => openDecide(a, 'returned')}><i className="bi bi-arrow-counterclockwise" /></button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
          </>
        )}
      </div>

      <Modal show={!!decideTarget} onClose={() => setDecideTarget(null)} title={decideTarget ? `Mark assessment as ${assessmentStatusBadgeMap[decideTarget.status]?.label || decideTarget.status}` : ''}>
        {decideTarget && (
          <>
            <p style={{ fontSize: 13, color: 'var(--sms-txt2)' }}>
              {decideTarget.assessment.trainer_name} · {decideTarget.assessment.module_name || decideTarget.assessment.module_code || '—'} · {decideTarget.assessment.title}
            </p>
            {decideTarget.status === 'returned' && (
              <>
                <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Remarks *</label>
                <textarea className="sms-form-control" rows={3} value={decideRemarks} onChange={e => setDecideRemarks(e.target.value)}
                  placeholder="Explain what needs to be corrected before resubmitting" />
              </>
            )}
            <div className="d-flex gap-2 justify-content-end mt-3">
              <button type="button" className="sms-btn sms-btn-outline" onClick={() => setDecideTarget(null)}><i className="bi bi-x-lg" /> Cancel</button>
              <button type="button" className="sms-btn sms-btn-primary" disabled={saving || (decideTarget.status === 'returned' && !decideRemarks.trim())} onClick={handleDecide}>
                {saving ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> Confirm</>}
              </button>
            </div>
          </>
        )}
      </Modal>

      <DetailModal show={!!viewing} onClose={() => setViewing(null)} title="Assessment Scores" size="lg" fields={viewing ? [
        { label: 'Trainer', value: viewing.trainer_name },
        { label: 'Module',  value: viewing.module_name || viewing.module_code || '—' },
        { label: 'Intake',  value: viewing.intake_code || '—' },
        { label: 'Title',   value: viewing.title || '—' },
        { label: 'Status',  value: <Badge value={viewing.status} map={assessmentStatusBadgeMap} /> },
        ...(viewing?.status === 'returned' && viewing.remarks ? [{ label: 'Remarks', value: viewing.remarks }] : []),
      ] : []}>
        {!scoresLoading && scores.length === 0 ? <EmptyState icon="bi-people" message="No enrolled students" /> : (
          <table className="sms-table">
            <thead><tr><th>Student</th><th>Enrolment</th><th className="num">Score</th></tr></thead>
            <tbody>
              {scoresLoading ? <TableSkeletonRows columns={3} /> : scores.map(s => (
                <tr key={s.student_no}>
                  <td>{s.name}</td>
                  <td style={{ textTransform: 'capitalize' }}>{s.enrolment_status}</td>
                  <td className="num">{s.score ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DetailModal>
    </>
  );
}

// ─── OBSERVATION LOGS ───────────────────────────────────────────────────────────
const OBS_EMPTY = { trainer_id: '', module_code: '', intake_code: '', session_id: '', observation_date: '', focus_area: '', rating: 'satisfactory', remarks: '' };

export function TrainerObservationsPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows, setRows] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [modules, setModules] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [intakeModules, setIntakeModules] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(OBS_EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [trainerFilter, setTrainerFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/trainers/observations').then(r => setRows(r.data.data || []))
      .catch(() => toast('Failed to load observations', 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get('/trainers').then(r => setTrainers(r.data.data || [])).catch(() => {});
    // Only active modules/intakes are offered when logging a new observation —
    // past observations (loaded above) can still reference an since-deactivated
    // one, since listObservations is never filtered by is_active.
    api.get('/trainers/observations/options').then(r => {
      setModules(r.data.data?.modules || []);
      setIntakes(r.data.data?.intakes || []);
      setIntakeModules(r.data.data?.intakeModules || []);
    }).catch(() => {});
  }, []);

  const q = search.trim().toLowerCase();
  const filtered = rows.filter(o =>
    (!trainerFilter || String(o.trainer_id) === String(trainerFilter)) &&
    (!ratingFilter || o.rating === ratingFilter) &&
    (!q ||
      (o.trainer_name || '').toLowerCase().includes(q) ||
      (o.module_name || '').toLowerCase().includes(q) ||
      (o.module_code || '').toLowerCase().includes(q) ||
      (o.focus_area || '').toLowerCase().includes(q) ||
      (o.observed_by_name || '').toLowerCase().includes(q) ||
      (o.remarks || '').toLowerCase().includes(q)
    )
  );
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);

  const openCreate = () => { setForm(OBS_EMPTY); setError(''); setShow(true); };
  const clearFilters = () => { setSearch(''); setTrainerFilter(''); setRatingFilter(''); };

  // Module choices narrow to whatever's actually assigned to the selected intake
  // (via intake_modules) — nothing to pick from until an intake is chosen.
  const modulesForIntake = form.intake_code
    ? modules.filter(m => intakeModules.some(im => im.intake_code === form.intake_code && im.module_code === m.module_code))
    : [];

  // Session/class choices come from the trainer's own attendance_sessions
  // (tmisqr) — refetched whenever the selected trainer changes, since the
  // picker is "optional" and only makes sense once we know whose sessions to
  // list. Only fetched while the create modal is open.
  useEffect(() => {
    if (!show || !form.trainer_id) { setSessions([]); return; }
    api.get('/trainers/observations/sessions', { params: { trainer_id: form.trainer_id } })
      .then(r => setSessions(r.data.data || []))
      .catch(() => setSessions([]));
  }, [show, form.trainer_id]);

  const sessionLabel = s => {
    const name = s.module_name || s.schedule_name;
    const date = s.session_date ? fmtDate(s.session_date) : '';
    return date ? `${name} — ${date}` : name;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.trainer_id || !form.observation_date) { setError('Trainer and observation date are required.'); return; }
    setSaving(true);
    try {
      await api.post('/trainers/observations', form);
      toast('Observation logged', 'success');
      setShow(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to log observation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title="Observation Logs" subtitle={`${total} of ${rows.length} observations`}
        actions={can('trainer_observations', 'create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Log Observation</button>} />

      <div className="sms-card mb-3" style={{ padding: '12px 14px' }}>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200 }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--sms-txt3)', fontSize: 13 }} />
            <input className="sms-form-control" style={{ paddingLeft: 28 }}
              placeholder="Search trainer, module, focus area, observer, remarks…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <SearchableSelect value={trainerFilter} onChange={e => setTrainerFilter(e.target.value)} placeholder="All trainers"
              options={trainers.map(t => ({ value: t.id, label: t.full_name, right: t.email }))} />
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 160 }}>
            <SearchableSelect value={ratingFilter} onChange={e => setRatingFilter(e.target.value)} placeholder="All ratings"
              options={['excellent', 'satisfactory', 'needs_improvement'].map(r => ({ value: r, label: observationRatingBadgeMap[r]?.label || r }))} />
          </div>
          <button className="sms-btn sms-btn-outline-danger" style={{ flex: '0 0 auto' }} onClick={clearFilters}>
            <i className="bi bi-x-circle" /> Clear
          </button>
        </div>
      </div>

      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? <EmptyState icon="bi-eye" message="No observations found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{ width: 40 }}>#</th>
                <SortTh label="Trainer" field="trainer_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Date" field="observation_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Intake</th>
                <th>Module</th>
                <th>Session / Class</th>
                <th>Observed By</th>
                <th>Focus Area</th>
                <th>Rating</th>
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={9} /> : paged.map((o, idx) => (
                  <tr key={o.id}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{o.trainer_name}</td>
                    <td>{fmtDate(o.observation_date)}</td>
                    <td>{o.intake_code || '—'}</td>
                    <td>{o.module_name || o.module_code || '—'}</td>
                    <td>
                      {o.session_schedule_name ? `${o.session_schedule_name}${o.session_date ? ` — ${fmtDate(o.session_date)}` : ''}` : '—'}
                    </td>
                    <td>{o.observed_by_name}</td>
                    <td>{o.focus_area || '—'}</td>
                    <td><Badge value={o.rating} map={observationRatingBadgeMap} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
          </>
        )}
      </div>

      <Modal show={show} onClose={() => setShow(false)} title="Log Observation">
        <form onSubmit={handleSubmit} noValidate>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
          <div className="row g-3">
            <div className="col-12">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Trainer *</label>
              <SearchableSelect value={form.trainer_id} onChange={e => setForm(f => ({ ...f, trainer_id: e.target.value }))} placeholder="-- Select trainer --"
                options={trainers.map(t => ({ value: t.id, label: t.full_name, right: t.email }))} />
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Observation Date *</label>
              <input type="date" className="sms-form-control" value={form.observation_date} onChange={e => setForm(f => ({ ...f, observation_date: e.target.value }))} />
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Rating *</label>
              <select className="sms-form-control" value={form.rating} onChange={e => setForm(f => ({ ...f, rating: e.target.value }))}>
                <option value="excellent">Excellent</option>
                <option value="satisfactory">Satisfactory</option>
                <option value="needs_improvement">Needs Improvement</option>
              </select>
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Intake</label>
              <SearchableSelect value={form.intake_code}
                onChange={e => setForm(f => ({ ...f, intake_code: e.target.value, module_code: '' }))}
                placeholder="-- Select intake --"
                options={intakes.map(i => ({ value: i.intake_code, label: i.intake_code }))} />
            </div>
            <div className="col-12 col-sm-6">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Module</label>
              <SearchableSelect value={form.module_code} onChange={e => setForm(f => ({ ...f, module_code: e.target.value }))}
                placeholder={form.intake_code ? '-- Select module --' : '-- Select an intake first --'}
                disabled={!form.intake_code}
                options={modulesForIntake.map(m => ({ value: m.module_code, label: m.module_name, right: m.module_code }))} />
            </div>
            <div className="col-12">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Session / Class (optional)</label>
              <SearchableSelect value={form.session_id} onChange={e => setForm(f => ({ ...f, session_id: e.target.value }))}
                placeholder={form.trainer_id ? '-- Select session --' : '-- Select a trainer first --'}
                disabled={!form.trainer_id}
                options={sessions.map(s => ({ value: s.id, label: sessionLabel(s) }))} />
            </div>
            <div className="col-12">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Focus Area</label>
              <input className="sms-form-control" value={form.focus_area} onChange={e => setForm(f => ({ ...f, focus_area: e.target.value }))} placeholder="e.g. Classroom management" />
            </div>
            <div className="col-12">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Remarks</label>
              <textarea className="sms-form-control" rows={3} value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-end mt-3">
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setShow(false)}><i className="bi bi-x-lg" /> Cancel</button>
            <button type="submit" className="sms-btn sms-btn-primary" disabled={saving}>
              {saving ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> Save</>}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// ─── FEE RATES ──────────────────────────────────────────────────────────────────
// Admin-side setup of a trainer's fee per hour. Attendance rates are scoped
// to course + intake + module; preview/interview rates are trainer-level.
const FEE_RATE_EMPTY = { trainer_id: '', trainer_ids: [], fee_type: 'attendance', assessment_basis: 'duration', course_code: '', intake_code: '', module_code: '', rate_per_hour: '', start_date: '', end_date: '' };

// Assessment rates are trainer-level (like Course Preview / Interview) and
// split by basis. Per Duration shows as plain "Assessment"; Per Pax is paid
// per student, the other two per hour.
const FEE_RATE_SCOPED_TYPES = ['attendance'];
const ASSESSMENT_BASIS_MAP = {
  duration: 'None',
  per_pax: 'Assessment F.A. 1.2.',
  final: 'Assessment F.A. 2.2.',
};
const isPerPaxRate = r => r.fee_type === 'interview' || (r.fee_type === 'assessment' && r.assessment_basis === 'per_pax');
const feeRateTypeLabel = r => r.fee_type === 'assessment' && r.assessment_basis && r.assessment_basis !== 'duration'
  ? ASSESSMENT_BASIS_MAP[r.assessment_basis]
  : FEE_RATE_TYPE_MAP[r.fee_type || 'attendance'];

const FEE_RATE_TYPE_MAP = {
  attendance: 'Attendance',
  assessment: 'Assessment',
  course_preview: 'Course Preview',
  interview: 'Interview',
};

const FEE_RATE_STATUS_MAP = {
  new:        { label: 'New',        cls: 'badge-active' },
  active:     { label: 'Active',     cls: 'badge-present' },
  expired:    { label: 'Expired',    cls: 'badge-inactive' },
  terminated: { label: 'Terminated', cls: 'badge-absent' },
};

const FEE_RATE_TYPE_BADGE_MAP = {
  attendance:    { label: 'Attendance', cls: 'badge-present' },
  assessment:    { label: 'Assessment', cls: 'badge-graduated' },
  assessment_per_pax: { label: 'Assessment F.A. 1.2.', cls: 'badge-fee-pax' },
  assessment_final:   { label: 'Assessment F.A. 2.2.', cls: 'badge-fee-final' },
  course_preview:{ label: 'Course Preview', cls: 'badge-active' },
  interview:     { label: 'Interview', cls: 'badge-late' },
};

// ─── SCHEDULE BOARD ─────────────────────────────────────────────────────────────
// Weekly grid of every trainer's sessions (read-only — sessions live in the
// external QR attendance system, this app never creates/edits them), with
// trainer/venue double-booking flagged server-side (trainerSchedule.service.js
// detectConflicts). Defaults to the current week; Previous/Next/date-jump
// navigate to any other week. Clicking a card opens the roster (who clocked
// in/out) via the shared RosterModal.
export function TrainerScheduleBoardPage() {
  const [mode, setMode] = useState('week');
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [rosterSession, setRosterSession] = useState(null);
  const [live, setLive] = useState(false);

  const range = getRangeForMode(mode, anchorDate);
  const { from, to } = range;
  const isCurrent = from === getRangeForMode(mode, new Date()).from;

  const shift = (dir) => setAnchorDate(d => shiftAnchor(mode, d, dir));
  const goToDay = (date) => { setAnchorDate(date); setMode('day'); };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api.get(`/trainers/schedule?from_date=${from}&to_date=${to}`)
      .then(r => { if (!cancelled) setRows(r.data.data || []); })
      .finally(() => { if (!cancelled) setLoading(false); });

    if (!socket.connected) socket.connect();
    const onConnect = () => {
      socket.emit('join_admin_schedule', { from_date: from, to_date: to }, (res) => {
        if (cancelled) return;
        if (res?.success) { setRows(res.data || []); setLoading(false); setLive(true); }
      });
    };
    const onScheduleUpdate = (data) => { if (!cancelled) setRows(data || []); };
    const onDisconnect = () => setLive(false);

    socket.on('connect', onConnect);
    socket.on('admin_schedule_update', onScheduleUpdate);
    socket.on('disconnect', onDisconnect);
    if (socket.connected) onConnect();

    return () => {
      cancelled = true;
      socket.emit('leave_admin_schedule', { from_date: from, to_date: to });
      socket.off('connect', onConnect);
      socket.off('admin_schedule_update', onScheduleUpdate);
      socket.off('disconnect', onDisconnect);
    };
  }, [from, to]);

  return (
    <>
      <PageHeader title="Weekly Schedule" subtitle={`${rows.length} sessions`} />
      <div className="sms-card">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ gap: 10 }}>
          <h3 className="d-flex align-items-center gap-2" style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
            {scheduleRangeLabel(mode, anchorDate, range)}
            {live && <span className="badge rounded-pill badge-present" style={{ fontSize: 12 }}>
              <i className="bi bi-broadcast" /> Live
            </span>}
          </h3>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <ViewModeToggle mode={mode} onChange={setMode} />
            <input
              type="date"
              className="sms-form-control"
              style={{ width: 'auto' }}
              value={ymd(anchorDate)}
              onChange={e => { if (e.target.value) setAnchorDate(new Date(`${e.target.value}T00:00:00`)); }}
              aria-label="Jump to date"
            />
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => shift(-1)}>
              <i className="bi bi-chevron-left" /> Previous
            </button>
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setAnchorDate(new Date())} disabled={isCurrent}>
              {SCHEDULE_TODAY_LABEL[mode]}
            </button>
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => shift(1)}>
              Next <i className="bi bi-chevron-right" />
            </button>
          </div>
        </div>
        {mode === 'month' ? (
          <MonthGrid
            anchorDate={anchorDate}
            sessions={rows}
            loading={loading}
            onDayClick={goToDay}
            onSessionClick={setRosterSession}
            showTrainer
          />
        ) : (
          <ScheduleCardGrid
            columns={scheduleColumns(mode, anchorDate)}
            sessions={rows}
            loading={loading}
            onSessionClick={setRosterSession}
            showTrainer
          />
        )}
      </div>
      {rosterSession && (
        <RosterModal
          session={rosterSession}
          rosterUrl={`/trainers/schedule/${rosterSession.id}/roster`}
          onClose={() => setRosterSession(null)}
        />
      )}
    </>
  );
}

// Checkbox list with search, so one fee rate setup can be applied to several
// trainers at once. SearchableSelect only holds a single value, hence a
// separate control. `value` is an array of trainer ids (strings).
function TrainerMultiSelect({ value, onChange, trainers }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const visible = trainers.filter(t =>
    !q || (t.full_name || '').toLowerCase().includes(q) || (t.email || '').toLowerCase().includes(q));
  const selected = new Set(value.map(String));
  const allVisibleSelected = visible.length > 0 && visible.every(t => selected.has(String(t.id)));
  const someVisibleSelected = !allVisibleSelected && visible.some(t => selected.has(String(t.id)));

  const toggle = id => {
    const key = String(id);
    onChange(selected.has(key) ? value.filter(v => String(v) !== key) : [...value, key]);
  };
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      const hide = new Set(visible.map(t => String(t.id)));
      onChange(value.filter(v => !hide.has(String(v))));
    } else {
      onChange([...new Set([...value.map(String), ...visible.map(t => String(t.id))])]);
    }
  };

  return (
    <div className="trn-pick">
      <div className="trn-pick-box">
        <div className="trn-pick-search">
          <i className="bi bi-search" aria-hidden="true" />
          <input type="text" placeholder="Search by name or email" value={query}
            onChange={e => setQuery(e.target.value)} aria-label="Search trainers" autoComplete="off" />
          {query && (
            <button type="button" className="trn-pick-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          )}
        </div>
        <label className="trn-pick-all">
          <input type="checkbox" checked={allVisibleSelected} disabled={!visible.length}
            ref={el => { if (el) el.indeterminate = someVisibleSelected; }} onChange={toggleAllVisible} />
          <span>{q ? 'Select all matching' : 'Select all'}</span>
          <span className="trn-pick-count">{visible.length}</span>
        </label>
        <div className="trn-pick-list" role="group" aria-label="Trainers">
          {visible.length === 0 && (
            <div className="trn-pick-empty"><i className="bi bi-person-x" aria-hidden="true" /> No trainers found</div>
          )}
          {visible.map(t => {
            const on = selected.has(String(t.id));
            return (
              <label key={t.id} className={`trn-pick-row${on ? ' on' : ''}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(t.id)} />
                <TrainerAvatar name={t.full_name} avatarUrl={t.avatar_url} size={30} />
                <span className="trn-pick-text">
                  <span className="trn-pick-name">{t.full_name}</span>
                  {t.email && <span className="trn-pick-email">{t.email}</span>}
                </span>
              </label>
            );
          })}
        </div>
      </div>
      <div className="trn-pick-foot">
        <span className={value.length ? 'trn-pick-foot-on' : ''}>
          {value.length ? `${value.length} trainer${value.length === 1 ? '' : 's'} selected` : 'No trainers selected'}
        </span>
        {value.length > 0 && (
          <button type="button" onClick={() => onChange([])}>Clear selection</button>
        )}
      </div>
    </div>
  );
}

export function TrainerFeeRatesPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [modules, setModules] = useState([]);
  const [intakeModules, setIntakeModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [editingId, setEditingId] = useState(null); // fee rate id being edited, or null for create
  const [editingHasClaims, setEditingHasClaims] = useState(false);
  const [form, setForm] = useState(FEE_RATE_EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [trainerFilter, setTrainerFilter] = useState([]); // selected trainer ids (strings)
  const [trainerPickerOpen, setTrainerPickerOpen] = useState(false);
  const trainerPickerRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [terminateTarget, setTerminateTarget] = useState(null);
  const appliedSetupKey = useRef('');
  const trainerFilterKey = trainerFilter.join(',');

  // Scoped to the picked trainers — nothing loads until the admin picks at
  // least one, rather than the page dumping every trainer's rates at once.
  const load = () => {
    if (!trainerFilter.length) { setRows([]); return; }
    setLoading(true);
    api.get('/trainers/fee-rates', { params: { trainer_ids: trainerFilterKey } })
      .then(r => setRows(r.data.data || []))
      .catch(() => toast('Failed to load fee rates', 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [trainerFilterKey]);
  useEffect(() => {
    if (!trainerPickerOpen) return undefined;
    const onDown = e => { if (trainerPickerRef.current && !trainerPickerRef.current.contains(e.target)) setTrainerPickerOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [trainerPickerOpen]);
  useEffect(() => {
    api.get('/trainers').then(r => setTrainers(r.data.data || [])).catch(() => {});
    api.get('/trainers/fee-rates/options').then(r => {
      setCourses(r.data.data?.courses || []);
      setIntakes(r.data.data?.intakes || []);
      setModules(r.data.data?.modules || []);
      setIntakeModules(r.data.data?.intakeModules || []);
    }).catch(() => {});
  }, []);

  const feeRateSetup = location.state?.feeRateSetup;
  const feeRateSetupKey = feeRateSetup
    ? `${feeRateSetup.trainerId || ''}:${feeRateSetup.feeType || ''}`
    : '';
  useEffect(() => {
    if (!feeRateSetupKey || appliedSetupKey.current === feeRateSetupKey || !trainers.length) return;
    const trainer = trainers.find(t => String(t.id) === String(feeRateSetup.trainerId));
    if (!trainer) return;
    const feeType = FEE_RATE_TYPE_MAP[feeRateSetup.feeType] ? feeRateSetup.feeType : 'attendance';
    appliedSetupKey.current = feeRateSetupKey;
    setTrainerFilter([String(trainer.id)]);
    setSearch('');
    setEditingId(null);
    setEditingHasClaims(false);
    setForm({ ...FEE_RATE_EMPTY, trainer_id: String(trainer.id), trainer_ids: [String(trainer.id)], fee_type: feeType });
    setError('');
    setShow(true);
  }, [feeRateSetupKey, feeRateSetup, trainers]);

  const q = search.trim().toLowerCase();
  const filtered = rows.filter(r =>
    !q ||
    (r.trainer_name || '').toLowerCase().includes(q) ||
    (feeRateTypeLabel(r) || '').toLowerCase().includes(q) ||
    (r.course_name || '').toLowerCase().includes(q) ||
    (r.intake_code || '').toLowerCase().includes(q) ||
    (r.module_name || '').toLowerCase().includes(q)
  );
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);

  const openCreate = () => { setEditingId(null); setEditingHasClaims(false); setForm({ ...FEE_RATE_EMPTY, trainer_id: trainerFilter[0] || '', trainer_ids: trainerFilter }); setError(''); setShow(true); };
  const openEdit = row => {
    setEditingId(row.id);
    setEditingHasClaims(Boolean(Number(row.has_submitted_claim)));
    setForm({
      trainer_id: row.trainer_id, fee_type: row.fee_type || 'attendance', assessment_basis: row.assessment_basis || 'duration', course_code: row.course_code || '', intake_code: row.intake_code || '', module_code: row.module_code || '',
      rate_per_hour: row.rate_per_hour, start_date: row.start_date || '', end_date: row.end_date || '',
    });
    setError('');
    setShow(true);
  };
  const clearFilters = () => { setSearch(''); setTrainerFilter([]); };

  // Intake choices narrow to the selected course; module choices narrow to
  // whatever's actually assigned to the selected intake (via intake_modules)
  // — same cascade as the Observation Logs form.
  const intakesForCourse = form.course_code ? intakes.filter(i => i.course_code === form.course_code) : [];
  const modulesForIntake = form.intake_code
    ? modules.filter(m => intakeModules.some(im => im.intake_code === form.intake_code && im.module_code === m.module_code))
    : [];
  const isAttendanceRate = form.fee_type === 'attendance';
  const isAssessmentRate = form.fee_type === 'assessment';
  const isScopedRate = FEE_RATE_SCOPED_TYPES.includes(form.fee_type);
  const perPax = isPerPaxRate(form);
  const canEditRate = !editingId || !editingHasClaims;

  const handleSubmit = async e => {
    e.preventDefault();
    if (editingHasClaims) {
      setError('This fee rate is locked because a claim has already been submitted for it. Terminate it and add a new rate instead.');
      return;
    }
    if ((editingId ? !form.trainer_id : !form.trainer_ids.length) || !form.fee_type || !form.rate_per_hour) {
      setError('Trainer, fee type and rate are required.');
      return;
    }
    if (isScopedRate && (!form.course_code || !form.intake_code || !form.module_code)) {
      setError(`Course, intake and module are required for ${FEE_RATE_TYPE_MAP[form.fee_type]}.`);
      return;
    }
    if (isAssessmentRate && !form.assessment_basis) {
      setError('Select the assessment basis.');
      return;
    }
    if (!form.start_date || !form.end_date) {
      setError('Select the date range this rate applies to — only attendance sessions within it will be accepted for this rate.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/trainers/fee-rates/${editingId}`, {
          fee_type: form.fee_type, assessment_basis: form.assessment_basis, course_code: form.course_code, intake_code: form.intake_code, module_code: form.module_code,
          rate_per_hour: form.rate_per_hour, start_date: form.start_date, end_date: form.end_date,
        });
        toast('Fee rate updated', 'success');
      } else {
        const { trainer_id: _single, trainer_ids, ...rest } = form;
        const { data } = await api.post('/trainers/fee-rates', { ...rest, trainer_ids });
        toast(data.message || 'Fee rate added', 'success');
        if (data.skipped?.length) {
          toast(`Skipped ${data.skipped.map(s => `${s.name} (${s.reason})`).join('; ')}`, 'error');
        }
      }
      setShow(false);
      load();
    } catch (err) {
      const skipped = err.response?.data?.skipped;
      const base = err.response?.data?.message || 'Save failed';
      setError(skipped?.length > 1
        ? `${base}: ${skipped.map(s => `${s.name} (${s.reason})`).join('; ')}`
        : base);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/trainers/fee-rates/${deleteTarget.id}`);
      toast('Fee rate deleted', 'success');
    } catch (err) {
      toast(err.response?.data?.message || 'Delete failed', 'error');
    } finally {
      setDeleteTarget(null);
      load();
    }
  };

  const handleTerminate = async () => {
    try {
      await api.put(`/trainers/fee-rates/${terminateTarget.id}/terminate`);
      toast('Fee rate terminated', 'success');
    } catch (err) {
      toast(err.response?.data?.message || 'Terminate failed', 'error');
    } finally {
      setTerminateTarget(null);
      load();
    }
  };

  return (
    <>
      <PageHeader title="Fee Rates" subtitle={`${total} of ${rows.length} rates`}
        actions={<>
          <button type="button" className="sms-btn sms-btn-setup-guide" onClick={() => setShowGuide(v => !v)} aria-expanded={showGuide}>
            <i className={`bi ${showGuide ? 'bi-chevron-up' : 'bi-question-circle'}`} aria-hidden="true" />
            {showGuide ? 'Hide setup guide' : 'How fee rates work'}
          </button>
          {can('trainer_fee_rates', 'create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Add Rate</button>}
        </>} />

      {showGuide && (
        <section className="fee-rate-guide" aria-labelledby="fee-rate-guide-title">
          <div className="fee-rate-guide-header">
            <div className="fee-rate-guide-icon" aria-hidden="true"><i className="bi bi-lightbulb" /></div>
            <div className="fee-rate-guide-heading">
              <h6 id="fee-rate-guide-title">How fee rates work</h6>
              <p>Set the amount a trainer can claim by matching the fee type and the claim date.</p>
            </div>
            <button type="button" className="fee-rate-guide-dismiss" onClick={() => setShowGuide(false)} aria-label="Hide fee rate guide">
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>
          <div className="fee-rate-guide-steps">
            <article className="fee-rate-guide-step">
              <span className="fee-rate-guide-step-number" aria-hidden="true">1</span>
              <div><strong>Choose the scope</strong><p>Attendance needs a course, intake, and module. Course Preview and Interview are trainer-level rates.</p></div>
            </article>
            <article className="fee-rate-guide-step">
              <span className="fee-rate-guide-step-number" aria-hidden="true">2</span>
              <div><strong>Set the date range</strong><p>The start and end dates are inclusive. A claim uses the rate covering its claim date.</p></div>
            </article>
            <article className="fee-rate-guide-step">
              <span className="fee-rate-guide-step-number" aria-hidden="true">3</span>
              <div><strong>Keep ranges separate</strong><p>Only one active rate can cover the same trainer and scope. Overlapping ranges are blocked.</p></div>
            </article>
            <article className="fee-rate-guide-step">
              <span className="fee-rate-guide-step-number" aria-hidden="true">4</span>
              <div><strong>Amend safely</strong><p>Used rates are locked. Terminate the old rate and add a new one; existing claims keep their saved rate.</p></div>
            </article>
          </div>
          <div className="fee-rate-guide-footer">
            <span><i className="bi bi-clock-history" aria-hidden="true" /> <strong>Automatic expiry:</strong> the server checks every minute and closes rates after their end date.</span>
            <span><i className="bi bi-info-circle" aria-hidden="true" /> <strong>Status:</strong> New, Active, Expired, or Terminated.</span>
          </div>
        </section>
      )}

      <div className="sms-card mb-3" style={{ padding: '12px 14px' }}>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div ref={trainerPickerRef} style={{ position: 'relative', flex: '1 1 220px', minWidth: 200 }}>
            <button type="button" className="sms-form-control d-flex align-items-center justify-content-between"
              style={{ textAlign: 'left', cursor: 'pointer' }} aria-expanded={trainerPickerOpen}
              onClick={() => setTrainerPickerOpen(o => !o)}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {trainerFilter.length === 0 ? '-- Select trainers --'
                  : trainerFilter.length === 1 ? (trainers.find(t => String(t.id) === trainerFilter[0])?.full_name || '1 trainer selected')
                  : `${trainerFilter.length} trainers selected`}
              </span>
              <i className={`bi bi-chevron-${trainerPickerOpen ? 'up' : 'down'}`} style={{ fontSize: 12 }} />
            </button>
            {trainerPickerOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 30, minWidth: 280 }}>
                <TrainerMultiSelect value={trainerFilter} trainers={trainers} onChange={setTrainerFilter} />
              </div>
            )}
          </div>
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200 }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--sms-txt3)', fontSize: 13 }} />
            <input className="sms-form-control" style={{ paddingLeft: 28 }}
              placeholder="Search course, intake, module…" disabled={!trainerFilter.length}
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="sms-btn sms-btn-outline-danger" style={{ flex: '0 0 auto' }} onClick={clearFilters}>
            <i className="bi bi-x-circle" /> Clear
          </button>
        </div>
      </div>

      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!trainerFilter.length ? (
          <EmptyState icon="bi-person-badge" message="Select one or more trainers above to view their fee rates" />
        ) : !loading && filtered.length === 0 ? <EmptyState icon="bi-currency-dollar" message={trainerFilter.length === 1 ? 'No fee rates set up for this trainer yet' : 'No fee rates set up for these trainers yet'} /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{ width: 40 }}>#</th>
                <SortTh label="Trainer" field="trainer_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Fee Type</th>
                <th>Course</th>
                <th>Intake</th>
                <th>Module</th>
                <th>Date Range</th>
                <th className="num">Rate</th>
                <th>Status</th>
                {(can('trainer_fee_rates', 'update') || can('trainer_fee_rates', 'delete')) && <th style={{ width: 100 }}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={9 + ((can('trainer_fee_rates', 'update') || can('trainer_fee_rates', 'delete')) ? 1 : 0)} /> : paged.map((r, idx) => (
                  <tr key={r.id}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{r.trainer_name}</td>
                    <td><Badge value={r.fee_type === 'assessment' && r.assessment_basis && r.assessment_basis !== 'duration' ? `assessment_${r.assessment_basis}` : (r.fee_type || 'attendance')} map={FEE_RATE_TYPE_BADGE_MAP} /></td>
                    <td>{r.course_name || r.course_code || '—'}</td>
                    <td className="fee-rate-sub">{r.intake_code || '—'}</td>
                    <td>
                      {r.module_name ? (
                        <>
                          <div>{r.module_name}</div>
                          {r.module_code && <div className="fee-rate-module-code"><i className="bi bi-code-square" aria-hidden="true" /> {r.module_code}</div>}
                        </>
                      ) : (r.module_code || '—')}
                    </td>
                    <td className="fee-rate-sub">{fmtDate(r.start_date)} – {fmtDate(r.end_date)}</td>
                    <td className="num">{fmtMoney(r.rate_per_hour)} <span className="fee-rate-unit" style={{ fontSize: 12 }}>/ {isPerPaxRate(r) ? 'pax' : 'hr'}</span></td>
                    <td>
                      <Badge value={r.computed_status} map={FEE_RATE_STATUS_MAP} />
                      {r.terminated_at && ['expired', 'terminated'].includes(r.computed_status) && (
                        <div className="fee-rate-closed-at">
                          <i className="bi bi-clock-history" aria-hidden="true" />
                          {r.computed_status === 'expired' ? 'Expired' : 'Terminated'} {fmtDateTime(r.terminated_at)}
                        </div>
                      )}
                    </td>
                    {(can('trainer_fee_rates', 'update') || can('trainer_fee_rates', 'delete')) && (
                      <td><div className="d-flex gap-1">
                        {can('trainer_fee_rates', 'update') && !['expired', 'terminated'].includes(r.computed_status) && (
                          <>
                            <button className="sms-btn sms-btn-outline" style={{ padding: '4px 6px' }} title={Number(r.has_submitted_claim) ? 'View locked rate' : 'Edit'} onClick={() => openEdit(r)}><i className={`bi bi-${Number(r.has_submitted_claim) ? 'lock' : 'pencil'}`} /></button>
                            <button className="sms-btn sms-btn-outline-danger" style={{ padding: '4px 6px' }} title="Terminate" onClick={() => setTerminateTarget(r)}><i className="bi bi-stop-circle" /></button>
                          </>
                        )}
                        {can('trainer_fee_rates', 'delete') && !Number(r.has_submitted_claim) && (
                          <button className="sms-btn sms-btn-outline-danger" style={{ padding: '4px 6px' }} title="Delete" onClick={() => setDeleteTarget(r)}><i className="bi bi-trash" /></button>
                        )}
                      </div></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
          </>
        )}
      </div>

      <Modal show={show} onClose={() => setShow(false)} title={editingId ? (editingHasClaims ? 'View Locked Fee Rate' : 'Edit Fee Rate') : 'Add Fee Rate'}>
        <form onSubmit={handleSubmit} noValidate>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
          <div className="d-flex flex-column" style={{ gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>{editingId ? 'Trainer *' : 'Trainers *'}</label>
              {editingId ? (
                <SearchableSelect value={form.trainer_id} onChange={() => {}}
                  placeholder="-- Select trainer --" disabled
                  options={trainers.map(t => ({ value: t.id, label: t.full_name, right: t.email }))} />
              ) : (
                <TrainerMultiSelect value={form.trainer_ids} trainers={trainers}
                  onChange={ids => setForm(f => ({ ...f, trainer_ids: ids }))} />
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Fee Type *</label>
              <div role="radiogroup" aria-label="Fee Type" className="fee-rate-type-options"
                style={{ '--fee-rate-index': Math.max(0, Object.keys(FEE_RATE_TYPE_MAP).indexOf(form.fee_type)) }}>
                <span className="fee-rate-type-slider" aria-hidden="true" />
                {Object.entries(FEE_RATE_TYPE_MAP).map(([value, label]) => (
                  <label key={value} className={`fee-rate-type-option${form.fee_type === value ? ' selected' : ''}`}>
                    <input type="radio" name="fee_type" value={value} checked={form.fee_type === value} disabled={!canEditRate}
                      onChange={e => setForm(f => ({
                        ...FEE_RATE_EMPTY,
                        trainer_id: f.trainer_id,
                        trainer_ids: f.trainer_ids,
                        fee_type: e.target.value,
                      }))} />
                    <span className="fee-rate-type-dot" aria-hidden="true" />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <div className="fee-rate-type-help">
                <i className={`bi ${isScopedRate ? 'bi-diagram-3' : 'bi-person-badge'}`} aria-hidden="true" />
                {isScopedRate
                  ? `${FEE_RATE_TYPE_MAP[form.fee_type]} is matched to one course, intake, and module.`
                  : `${FEE_RATE_TYPE_MAP[form.fee_type]} is matched to this trainer by claim date.`}
              </div>
              {isAssessmentRate && (
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Assessment Basis *</label>
                  <select className="sms-form-control" value={form.assessment_basis} disabled={!canEditRate}
                    onChange={e => setForm(f => ({ ...f, assessment_basis: e.target.value }))}>
                    {Object.entries(ASSESSMENT_BASIS_MAP).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
              )}
              {editingId && editingHasClaims && <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 5 }}>
                This rate is locked because a claim has already been submitted. Terminate it and add a new rate to make an amendment; existing claims keep this saved rate.
              </div>}
            </div>
            {isScopedRate && <>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Course *</label>
              <SearchableSelect value={form.course_code}
                onChange={e => setForm(f => ({ ...f, course_code: e.target.value, intake_code: '', module_code: '' }))}
                placeholder="-- Select course --" disabled={!canEditRate}
                options={courses.map(c => ({ value: c.course_code, label: c.course_name }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Intake *</label>
              <SearchableSelect value={form.intake_code}
                onChange={e => {
                  const picked = intakesForCourse.find(i => i.intake_code === e.target.value);
                  setForm(f => ({
                    ...f, intake_code: e.target.value, module_code: '',
                    // Pre-fill the effective range with the intake's own SOC/EOC —
                    // still just a starting point, the admin can narrow it below.
                    start_date: picked?.soc_date ? picked.soc_date.slice(0, 10) : f.start_date,
                    end_date: picked?.eoc_date ? picked.eoc_date.slice(0, 10) : f.end_date,
                  }));
                }}
                placeholder={form.course_code ? '-- Select intake --' : '-- Select a course first --'}
                disabled={!canEditRate || !form.course_code}
                options={intakesForCourse.map(i => ({ value: i.intake_code, label: i.intake_code, right: `${fmtDate(i.soc_date)} – ${fmtDate(i.eoc_date)}` }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Module *</label>
              <SearchableSelect value={form.module_code} onChange={e => setForm(f => ({ ...f, module_code: e.target.value }))}
                placeholder={form.intake_code ? '-- Select module --' : '-- Select an intake first --'}
                disabled={!canEditRate || !form.intake_code}
                options={modulesForIntake.map(m => ({ value: m.module_code, label: m.module_name, right: m.module_code }))} />
            </div>
            </>}
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Effective Date Range *</label>
              <DateRangePicker startDate={form.start_date} endDate={form.end_date} disabled={!canEditRate} alwaysBelow
                onChange={({ start, end }) => setForm(f => ({ ...f, start_date: start || '', end_date: end || '' }))} />
              <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 4 }}>
                {isScopedRate ? `Only ${isAssessmentRate ? 'assessment' : 'attendance'} sessions dated within this range count toward this rate.` : 'This rate applies to the selected fee type within this date range.'}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>{perPax ? 'Rate per pax (S$)' : 'Rate per hour (S$)'} *</label>
              <input type="number" min="0" step="0.01" className="sms-form-control" value={form.rate_per_hour} disabled={!canEditRate}
                onChange={e => setForm(f => ({ ...f, rate_per_hour: e.target.value }))} placeholder="0.00" />
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-end mt-4">
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setShow(false)}><i className="bi bi-x-lg" /> Cancel</button>
            {!editingHasClaims && <button type="submit" className="sms-btn sms-btn-primary" disabled={saving}>
              {saving ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> {!editingId && form.trainer_ids.length > 1 ? `Save ${form.trainer_ids.length} rates` : 'Save'}</>}
            </button>}
          </div>
        </form>
      </Modal>

      <ConfirmDialog show={!!deleteTarget} danger
        message={deleteTarget ? `Delete the ${feeRateTypeLabel(deleteTarget)} fee rate for ${deleteTarget.trainer_name}${deleteTarget.intake_code ? ` on ${deleteTarget.intake_code} / ${deleteTarget.module_name || deleteTarget.module_code}` : ''}?` : ''}
        onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />

      <ConfirmDialog show={!!terminateTarget} danger
        message={terminateTarget ? `Terminate the ${feeRateTypeLabel(terminateTarget)} fee rate for ${terminateTarget.trainer_name}${terminateTarget.intake_code ? ` on ${terminateTarget.intake_code} / ${terminateTarget.module_name || terminateTarget.module_code}` : ''}? This cannot be undone. Existing claims keep their saved rate; add a new rate if coverage needs to continue.` : ''}
        confirmLabel="Terminate"
        onConfirm={handleTerminate} onCancel={() => setTerminateTarget(null)} />
    </>
  );
}

// ─── MARKING PERMISSIONS ────────────────────────────────────────────────────────
// Admin-side setup of which trainer may enter component marks for a given
// intake + module + exam type (Main / Resit). The trainer portal's Component
// Marks screen only shows what's granted "Allowed" here — everything else
// stays out of that trainer's module list, not just disabled.
const examTypeLabel = { main: 'Main', resit: 'Resit' };
const rowKey = (module_code, exam_type) => `${module_code}:${exam_type}`;

// One Main or Resit "slot" inside a module card — trainer picker + permission
// switch, tagged with an accent color (blue for Main, gold for Resit) so the
// two read as distinct attempts rather than two rows of the same thing.
function ExamSlot({ examType, row, canUpdate, trainers, onChange, onRemove, invalid }) {
  const accent = examType === 'resit' ? 'var(--sms-gold)' : 'var(--sms-acc)';
  return (
    <div className="marking-perm-slot" style={{ borderLeftColor: invalid ? 'var(--sms-btn-outline-danger-txt)' : accent }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="marking-perm-slot-tag" style={{ color: accent, background: `color-mix(in srgb, ${accent} 14%, transparent)` }}>
          {examTypeLabel[examType]} exam
        </span>
        {onRemove && (
          <button type="button" className="marking-perm-slot-remove" disabled={!canUpdate}
            title={`Remove ${examTypeLabel[examType]} slot`} onClick={onRemove}>
            <i className="bi bi-x-lg" />
          </button>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginBottom: 4 }}>Trainer who enters marks</div>
      <SearchableSelect value={row.trainer_id} disabled={!canUpdate} placeholder="— unassigned —"
        onChange={e => onChange('trainer_id', e.target.value)}
        options={trainers.map(t => ({ value: t.id, label: t.full_name }))} />
      <div className="d-flex align-items-center justify-content-between mt-2">
        <span style={{ fontSize: 12, color: 'var(--sms-txt2)', fontWeight: 500 }}>Can enter marks</span>
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: 12, fontWeight: 650, color: row.is_allowed ? 'var(--sms-stat-green)' : 'var(--sms-btn-outline-danger-txt)' }}>
            {row.is_allowed ? 'Allowed' : 'Not Allowed'}
          </span>
          <button type="button" role="switch" aria-label={`Allow trainer to enter ${examTypeLabel[examType].toLowerCase()} marks`}
            aria-checked={!!row.is_allowed} disabled={!canUpdate}
            className="sms-switch" onClick={() => onChange('is_allowed', row.is_allowed ? 0 : 1)} />
        </div>
      </div>
      {invalid && (
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-btn-outline-danger-txt)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="bi bi-exclamation-triangle" /> Pick a trainer to keep this Allowed
        </div>
      )}
    </div>
  );
}

export function TrainerMarkingPermissionsPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [options, setOptions] = useState({ courses: [], intakes: [], intakeModules: [], trainers: [] });
  const [courseCode, setCourseCode] = useState('');
  const [intakeCode, setIntakeCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // rowsByModule: { [module_code]: { main: {trainer_id, is_allowed}, resit: {trainer_id, is_allowed} | undefined } }
  const [rowsByModule, setRowsByModule] = useState({});
  const [dirty, setDirty] = useState(false);
  const [invalidKeys, setInvalidKeys] = useState(new Set()); // `${module_code}:${exam_type}` slots flagged by the last save attempt
  const [setupModules, setSetupModules] = useState([]); // module_codes with an active Component Master setup for the selected intake
  const [setupTarget, setSetupTarget] = useState(null); // module being set up in the Component Setup modal, or null
  const canUpdate = can('trainer_marking_permissions', 'update');

  useEffect(() => {
    api.get('/trainers/marking-permissions/options').then(r => setOptions(r.data.data || { courses: [], intakes: [], intakeModules: [], trainers: [] }))
      .catch(() => toast('Failed to load setup options', 'error'));
  }, []);

  const intakesForCourse = courseCode ? options.intakes.filter(i => i.course_code === courseCode) : [];
  const modulesForIntake = intakeCode ? options.intakeModules.filter(im => im.intake_code === intakeCode) : [];

  const handleCourseChange = code => { setCourseCode(code); setIntakeCode(''); setRowsByModule({}); setDirty(false); setInvalidKeys(new Set()); };

  const emptyRow = () => ({ trainer_id: '', is_allowed: 0 });

  const buildRows = (modules, permissions) => {
    const byModule = {};
    for (const m of modules) {
      const savedMain = permissions.find(p => p.module_code === m.module_code && p.exam_type === 'main');
      const savedResit = permissions.find(p => p.module_code === m.module_code && p.exam_type === 'resit');
      byModule[m.module_code] = {
        main: savedMain ? { trainer_id: savedMain.trainer_id, is_allowed: savedMain.is_allowed } : emptyRow(),
        ...(savedResit ? { resit: { trainer_id: savedResit.trainer_id, is_allowed: savedResit.is_allowed } } : {}),
      };
    }
    return byModule;
  };

  const handleIntakeChange = code => {
    setIntakeCode(code);
    setRowsByModule({});
    setDirty(false);
    setInvalidKeys(new Set());
    setSetupModules([]);
    if (!code) return;
    setLoading(true);
    api.get('/trainers/marking-permissions', { params: { intake_code: code } })
      .then(r => {
        const { permissions = [], componentSetupModules = [] } = r.data.data || {};
        setRowsByModule(buildRows(options.intakeModules.filter(im => im.intake_code === code), permissions));
        setSetupModules(componentSetupModules);
      })
      .catch(() => toast('Failed to load marking permissions', 'error'))
      .finally(() => setLoading(false));
  };

  // Refreshes only which modules now have an active Component Master setup,
  // without touching rowsByModule — so finishing a setup from the "Set up
  // now" modal never discards whatever permission edits the admin already
  // made on this screen, and there's no need to re-pick course/intake.
  const refreshSetupModules = () => {
    if (!intakeCode) return;
    api.get('/trainers/marking-permissions', { params: { intake_code: intakeCode } })
      .then(r => setSetupModules(r.data.data?.componentSetupModules || []))
      .catch(() => {});
  };

  const updateRow = (module_code, exam_type, field, value) => {
    setDirty(true);
    setInvalidKeys(keys => {
      if (!keys.has(rowKey(module_code, exam_type))) return keys;
      const next = new Set(keys);
      next.delete(rowKey(module_code, exam_type));
      return next;
    });
    setRowsByModule(rows => {
      const current = rows[module_code] || { main: emptyRow() };
      return {
        ...rows,
        [module_code]: { ...current, [exam_type]: { ...(current[exam_type] || emptyRow()), [field]: value } },
      };
    });
  };

  const addResitRow = module_code => {
    setDirty(true);
    setRowsByModule(rows => {
      const current = rows[module_code] || { main: emptyRow() };
      return { ...rows, [module_code]: { main: current.main || emptyRow(), resit: emptyRow() } };
    });
  };

  const removeResitRow = module_code => {
    setDirty(true);
    setInvalidKeys(keys => {
      if (!keys.has(rowKey(module_code, 'resit'))) return keys;
      const next = new Set(keys);
      next.delete(rowKey(module_code, 'resit'));
      return next;
    });
    setRowsByModule(rows => {
      const current = rows[module_code] || { main: emptyRow() };
      return { ...rows, [module_code]: { main: current.main || emptyRow() } };
    });
  };

  const handleSave = async () => {
    const entries = [];
    const badKeys = new Set();
    const badLabels = [];
    for (const [module_code, exams] of Object.entries(rowsByModule)) {
      const moduleName = modulesForIntake.find(m => m.module_code === module_code)?.module_name || module_code;
      for (const [exam_type, row] of Object.entries(exams)) {
        // "Allowed" with no trainer picked is a dead permission — the server
        // would just delete it, silently discarding what looked like a save.
        if (row.is_allowed && !row.trainer_id) {
          badKeys.add(rowKey(module_code, exam_type));
          badLabels.push(`${moduleName} (${examTypeLabel[exam_type]})`);
        }
        entries.push({ module_code, exam_type, trainer_id: row.trainer_id || null, is_allowed: !!row.is_allowed });
      }
    }
    if (badKeys.size) {
      setInvalidKeys(badKeys);
      toast(`Select a trainer before marking as Allowed: ${badLabels.join(', ')}`, 'error');
      return;
    }
    setInvalidKeys(new Set());
    setSaving(true);
    try {
      await api.put('/trainers/marking-permissions', { intake_code: intakeCode, entries });
      toast('Marking permissions saved', 'success');
      setDirty(false);
      handleIntakeChange(intakeCode);
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to save permissions', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title="Trainer Marking Permissions"
        subtitle="Grant a trainer the right to enter marks for a module — only what's Allowed here shows up in their Component Marks list" />

      <div className="sms-card mb-3" style={{ padding: '14px 16px' }}>
        <div className="row g-3">
          <div className="col-12 col-sm-6 col-lg-4">
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Course</label>
            <SearchableSelect value={courseCode} placeholder="Select an active course…" onChange={e => handleCourseChange(e.target.value)}
              options={options.courses.map(c => ({ value: c.course_code, label: c.course_name }))} />
          </div>
          <div className="col-12 col-sm-6 col-lg-4">
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Intake</label>
            <SearchableSelect value={intakeCode} disabled={!courseCode} placeholder={courseCode ? 'Select an active intake…' : 'Select a course first'}
              onChange={e => handleIntakeChange(e.target.value)}
              options={intakesForCourse.map(i => ({
                value: i.intake_code, label: i.intake_code,
                sublabel: i.soc_date && i.eoc_date ? `${fmtDate(i.soc_date)} – ${fmtDate(i.eoc_date)}` : undefined,
              }))} />
          </div>
        </div>
      </div>

      <div className="sms-card mb-3" style={{ padding: '11px 16px', background: 'var(--sms-acc-bg)' }}>
        <div className="d-flex align-items-start gap-2">
          <i className="bi bi-info-circle" style={{ color: 'var(--sms-acc)', marginTop: 2 }} />
          <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
            <strong style={{ color: 'var(--sms-txt)', display: 'block', marginBottom: 2 }}>How to use</strong>
            <span>Choose a course and intake, select the trainer for each exam, turn on <strong>Can enter marks</strong>, then save.</span>
          </div>
        </div>
      </div>

      {!intakeCode ? (
        <div className="sms-card"><EmptyState icon="bi-person-check" message="Select a course and intake to set up marking permissions" /></div>
      ) : loading ? <div className="sms-card"><Spinner minHeight="240px" /></div> : !modulesForIntake.length ? (
        <div className="sms-card"><EmptyState icon="bi-collection" message="No modules are assigned to this intake yet" /></div>
      ) : (
        modulesForIntake.map((m, idx) => {
          const moduleRows = rowsByModule[m.module_code] || { main: emptyRow() };
          return (
            <div key={m.module_code} className="sms-card sms-widget-fade mb-3"
              style={{ padding: 0, overflow: 'hidden', animationDelay: `${Math.min(idx, 12) * 0.03}s` }}>
              <div className="d-flex align-items-center gap-2" style={{ padding: '12px 16px', borderBottom: '1px solid var(--sms-bdr)' }}>
                <div style={{
                  width: 24, height: 24, borderRadius: 7, background: 'var(--sms-sur2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: 'var(--sms-txt3)', flex: '0 0 auto',
                }}>{idx + 1}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 650 }}>{m.module_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--sms-txt3)', fontWeight: 500 }}>{m.module_code}</div>
                </div>
              </div>

              {!setupModules.includes(m.module_code) && (
                <div className="d-flex align-items-center gap-2" style={{
                  padding: '8px 16px', fontSize: 12, fontWeight: 500, color: 'var(--sms-gold)',
                  background: 'color-mix(in srgb, var(--sms-gold) 10%, transparent)',
                  borderBottom: '1px solid var(--sms-bdr)',
                }}>
                  <i className="bi bi-exclamation-triangle" />
                   No marking setup yet. Trainers cannot see this module until it is set up.
                  <button type="button" onClick={() => setSetupTarget({ module_code: m.module_code, module_name: m.module_name })}
                    style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', fontWeight: 650, textDecoration: 'underline', cursor: 'pointer', marginLeft: 'auto', whiteSpace: 'nowrap', fontSize: 12 }}>
                    Set up now →
                  </button>
                </div>
              )}

              <div className="d-flex flex-wrap gap-2" style={{ padding: '14px 16px' }}>
                <ExamSlot
                  examType="main" row={moduleRows.main || emptyRow()} canUpdate={canUpdate}
                  trainers={options.trainers} invalid={invalidKeys.has(rowKey(m.module_code, 'main'))}
                  onChange={(field, value) => updateRow(m.module_code, 'main', field, value)}
                />
                {moduleRows.resit ? (
                  <ExamSlot
                    examType="resit" row={moduleRows.resit} canUpdate={canUpdate}
                    trainers={options.trainers} invalid={invalidKeys.has(rowKey(m.module_code, 'resit'))}
                    onChange={(field, value) => updateRow(m.module_code, 'resit', field, value)}
                    onRemove={() => removeResitRow(m.module_code)}
                  />
                ) : canUpdate && (
                  <button type="button" onClick={() => addResitRow(m.module_code)}
                    className="marking-perm-ghost-slot" title={`Add a Resit slot for ${m.module_name}`}>
                    <i className="bi bi-plus-lg" /> Add Resit slot
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}

      {intakeCode && canUpdate && modulesForIntake.length > 0 && (
        <div className="sms-card mt-3 d-flex justify-content-between align-items-center" style={{ padding: '12px 16px', position: 'sticky', bottom: 16 }}>
          <div style={{ fontSize: 12.5, color: 'var(--sms-txt2)', display: 'flex', alignItems: 'center', gap: 8 }}>
            {dirty && <><span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--sms-acc)', display: 'inline-block' }} /> Unsaved changes</>}
          </div>
          <button type="button" className="sms-btn sms-btn-primary" disabled={saving || !dirty} onClick={handleSave}>
            {saving ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> Save Permissions</>}
          </button>
        </div>
      )}

      <ComponentSetupModal
        show={!!setupTarget}
        onClose={() => setSetupTarget(null)}
        moduleCode={setupTarget?.module_code}
        moduleLabel={setupTarget ? `${setupTarget.module_name} (${setupTarget.module_code})` : ''}
        allowCreate={false}
        onSaved={() => { setSetupTarget(null); refreshSetupModules(); }}
      />
    </>
  );
}
